import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAdminApi, VocabWord, PromptPreview, PromptSection, ChunkInfo, ChunkDetail, DeepScrapeRun, ConversationSummary, ManagedUser, KnowledgeSummaryMetadata, SuperadminOverview, SuperadminUsersResponse } from '../hooks/useAdminApi';
import SuperadminCostControlDashboard from './SuperadminCostControlDashboard';
import SuperadminAvatarManagement from './SuperadminAvatarManagement';
import SuperadminAvatarLab from './SuperadminAvatarLab';
import { isAgentAdminScopeReady } from '../utils/avatarProfiles';

type Tab = 'overview' | 'users' | 'behavior' | 'vocabulary' | 'knowledgebase' | 'conversations' | 'preview';
type SuperadminPanelTab = 'overview' | 'users' | 'avatars' | 'avatar-lab';

interface DocInfo {
  document_name: string;
  chunk_count: number;
  total_chars: number;
}

const countLines = (value?: string): number => {
  if (!value) return 0;
  return value.split(/\r?\n/).length || 1;
};

const isCallToActionsSection = (section: PromptSection): boolean =>
  section.label.startsWith('Call to Actions');

const deepScrapeStatusLabel = (status: DeepScrapeRun['status']): string => ({
  queued: 'Wartet',
  running: 'Läuft',
  succeeded: 'Erfolgreich',
  partial: 'Teilweise erfolgreich',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
}[status]);

const splitDeepScrapeList = (value: string): string[] => value
  .split(/[\n,]+/)
  .map(item => item.trim())
  .filter(Boolean);

const normalizeRouteSlug = (value: string | null | undefined, fallback = ''): string => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

function PromptTextBlock({
  content,
  isDarkMode,
}: {
  content: string;
  isDarkMode: boolean;
}) {
  const lines = content.split(/\r?\n/);
  return (
    <div className={`max-h-96 overflow-auto px-0 py-3 text-xs font-mono leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
      {lines.map((line, index) => (
        <div key={index} className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 px-5">
          <span className={`select-none text-right tabular-nums ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            {index + 1}
          </span>
          <span className="min-w-0 whitespace-pre-wrap break-words">{line || '\u00A0'}</span>
        </div>
      ))}
    </div>
  );
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return 'noch nie';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};

const formatAccountRole = (role: string | null | undefined): string => {
  switch (role) {
    case 'owner':
      return 'Owner / Superadmin';
    case 'superadmin':
      return 'Superadmin';
    case 'admin':
      return 'Plattform Admin';
    case 'user':
      return 'User';
    default:
      return role || 'unbekannt';
  }
};

const knowledgeSummaryMetadataFrom = (data: any): KnowledgeSummaryMetadata => ({
  generated_at: data?.metadata?.generated_at ?? data?.generated_at ?? null,
  source_hash: data?.metadata?.source_hash ?? data?.source_hash ?? null,
  document_count: data?.metadata?.document_count ?? data?.document_count ?? 0,
  chunk_count: data?.metadata?.chunk_count ?? data?.chunk_count ?? 0,
  selected_chunk_count: data?.metadata?.selected_chunk_count ?? data?.selected_chunk_count,
  source_char_count: data?.metadata?.source_char_count ?? data?.source_char_count ?? 0,
});

const wait = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms));

const ADMIN_DATA_LOCKED_MESSAGE = 'Admin-Daten wurden nicht vollständig geladen. Speichern ist deaktiviert, damit keine leeren Promptdaten überschrieben werden.';

function AdminPanel({
  isDarkMode,
  superadminOnly = false,
}: {
  isDarkMode: boolean;
  superadminOnly?: boolean;
}) {
  const api = useAdminApi();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customerSlug: routeCustomerSlugRaw, agentSlug: routeAgentSlugRaw } = useParams<{
    customerSlug?: string;
    agentSlug?: string;
  }>();
  const routeCustomerSlug = normalizeRouteSlug(routeCustomerSlugRaw);
  const routeAgentSlug = normalizeRouteSlug(routeAgentSlugRaw, 'sally');
  const hasRouteScope = Boolean(routeCustomerSlugRaw);
  const {
    isAuthenticated,
    username,
    adminSession,
    loading,
    error,
    login,
    logout,
    getAdminContext,
    switchAdminScope,
    getBehavior,
    getOnboardingMemory,
    getAdminProfile,
    saveBehavior,
    saveOnboardingMemory,
    saveAdminProfile,
    getUsers,
    getSuperadminOverview,
    getSuperadminUsers,
    downloadSuperadminUsageCsv,
    createUser,
    verifyUser,
    resetUserPassword,
    updateUserRole,
    updateUserEmail,
    deleteUser,
    getVocabulary,
    saveVocabulary,
    getPromptPreview,
    saveCorePrompt,
    getCallToActions,
    saveCallToActions,
    getKnowledgeSummary,
    saveKnowledgeSummary,
    generateKnowledgeSummary,
    getDocuments,
    getDocumentChunks,
    getChunkDetail,
    deleteChunk,
    deleteDocument,
    uploadDocument,
    getDeepScrapes,
    startDeepScrape,
    cancelDeepScrape,
    activateDeepScrapeRun,
    getConversations,
    deleteConversation,
  } = api;

  // Login state
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');

  // Behavior state
  const [behaviorText, setBehaviorText] = useState('');
  const [behaviorMode, setBehaviorMode] = useState<'blueprint' | 'custom'>('custom');
  const [setupBlueprintBehavior, setSetupBlueprintBehavior] = useState('');
  const [setupBlueprintRevision, setSetupBlueprintRevision] = useState<number | null>(null);
  const [blueprintCopyMessage, setBlueprintCopyMessage] = useState('');
  const [onboardingMemoryText, setOnboardingMemoryText] = useState('');
  const [behaviorSaved, setBehaviorSaved] = useState(false);
  const [onboardingMemorySaved, setOnboardingMemorySaved] = useState(false);
  const [adminDataLoading, setAdminDataLoading] = useState(false);
  const [adminDataLoaded, setAdminDataLoaded] = useState(false);
  const [adminDataLoadError, setAdminDataLoadError] = useState('');
  const [adminDisplayName, setAdminDisplayName] = useState('Daniel');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminProfileSaved, setAdminProfileSaved] = useState(false);
  const [managedUsers, setManagedUsers] = useState<ManagedUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userActionMessage, setUserActionMessage] = useState('');
  const [superadminPanelTab, setSuperadminPanelTab] = useState<SuperadminPanelTab>('overview');
  const [superadminOverview, setSuperadminOverview] = useState<SuperadminOverview | null>(null);
  const [superadminOverviewLoading, setSuperadminOverviewLoading] = useState(false);
  const [superadminUsers, setSuperadminUsers] = useState<SuperadminUsersResponse | null>(null);
  const [superadminUsersLoading, setSuperadminUsersLoading] = useState(false);
  const [usageCsvDownloading, setUsageCsvDownloading] = useState(false);
  const [usageCsvMessage, setUsageCsvMessage] = useState('');
  const [createUsername, setCreateUsername] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createRole, setCreateRole] = useState<'user' | 'admin' | 'superadmin'>('user');
  const [createVerified, setCreateVerified] = useState(true);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [emailEditUserId, setEmailEditUserId] = useState<string | null>(null);
  const [emailEditValue, setEmailEditValue] = useState('');
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState<string | null>(null);
  const [passwordResetUserId, setPasswordResetUserId] = useState<string | null>(null);
  const [passwordResetValue, setPasswordResetValue] = useState('');

  // Vocabulary state
  const [vocabWords, setVocabWords] = useState<VocabWord[]>([]);
  const [vocabSaved, setVocabSaved] = useState(false);
  const [vocabUpdatedAt, setVocabUpdatedAt] = useState<string | null>(null);
  const [vocabNotice, setVocabNotice] = useState('');
  const [newWord, setNewWord] = useState('');
  const [newContext, setNewContext] = useState('');
  const [newPronunciation, setNewPronunciation] = useState('');
  const [newInputAliases, setNewInputAliases] = useState('');
  const [newVoiceReplacement, setNewVoiceReplacement] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Preview state
  const [preview, setPreview] = useState<PromptPreview | null>(null);
  const [isPromptPreviewExtended, setIsPromptPreviewExtended] = useState(false);
  const [corePromptUnlocked, setCorePromptUnlocked] = useState(false);
  const [corePromptDraft, setCorePromptDraft] = useState('');
  const [corePromptSaving, setCorePromptSaving] = useState(false);
  const [corePromptMessage, setCorePromptMessage] = useState('');
  const [callToActionsUnlocked, setCallToActionsUnlocked] = useState(false);
  const [callToActionsDraft, setCallToActionsDraft] = useState('');
  const [callToActionsSaving, setCallToActionsSaving] = useState(false);
  const [callToActionsMessage, setCallToActionsMessage] = useState('');

  // Knowledge base state
  const [documents, setDocuments] = useState<DocInfo[]>([]);
  const [totalChunks, setTotalChunks] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [docChunks, setDocChunks] = useState<ChunkInfo[]>([]);
  const [expandedChunk, setExpandedChunk] = useState<ChunkDetail | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null); // chunk_id or 'doc:name'
  const [kbLoading, setKbLoading] = useState(false);
  const [knowledgeSummaryDraft, setKnowledgeSummaryDraft] = useState('');
  const [knowledgeSummarySavedText, setKnowledgeSummarySavedText] = useState('');
  const [knowledgeSummaryBaseText, setKnowledgeSummaryBaseText] = useState('');
  const [knowledgeSummaryMetadata, setKnowledgeSummaryMetadata] = useState<KnowledgeSummaryMetadata | undefined>(undefined);
  const [knowledgeSummaryMessage, setKnowledgeSummaryMessage] = useState('');
  const [knowledgeSummaryGenerating, setKnowledgeSummaryGenerating] = useState(false);
  const [knowledgeSummarySaving, setKnowledgeSummarySaving] = useState(false);
  // Upload state
  const [uploadDragging, setUploadDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'idle' | 'uploading' | 'success' | 'error'; message: string }>({ type: 'idle', message: '' });
  const uploadInputRef = useRef<HTMLInputElement>(null);
  // Versioned website imports for the active avatar.
  const [deepScrapeUrl, setDeepScrapeUrl] = useState('');
  const [deepScrapeMaxPages, setDeepScrapeMaxPages] = useState(250);
  const [deepScrapeMaxDepth, setDeepScrapeMaxDepth] = useState(8);
  const [deepScrapeIncludePdfs, setDeepScrapeIncludePdfs] = useState(false);
  const [deepScrapeSubdomains, setDeepScrapeSubdomains] = useState('');
  const [deepScrapeIncludePatterns, setDeepScrapeIncludePatterns] = useState('');
  const [deepScrapeExcludePatterns, setDeepScrapeExcludePatterns] = useState('');
  const [deepScrapeRuns, setDeepScrapeRuns] = useState<DeepScrapeRun[]>([]);
  const [deepScrapeLoading, setDeepScrapeLoading] = useState(false);
  const [deepScrapeMessage, setDeepScrapeMessage] = useState('');
  const [deepScrapeExpanded, setDeepScrapeExpanded] = useState(false);

  // Conversations state
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [convoLoading, setConvoLoading] = useState(false);
  const [convoSearch, setConvoSearch] = useState('');
  const [convoSearchActive, setConvoSearchActive] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState<Tab>(superadminOnly ? 'overview' : 'behavior');
  const deepLinkHandledRef = useRef<string | null>(null);
  const scopeSwitchInFlightRef = useRef(false);
  const switchAdminScopeRef = useRef(switchAdminScope);
  const [confirmedRouteScopeKey, setConfirmedRouteScopeKey] = useState('');
  const [scopeSwitching, setScopeSwitching] = useState(false);
  const [scopeSwitchError, setScopeSwitchError] = useState('');

  const d = isDarkMode;
  const routeScopeKey = hasRouteScope ? `${routeCustomerSlug}/${routeAgentSlug}` : '';

  const routeScopeReady = !hasRouteScope || isAgentAdminScopeReady(
    confirmedRouteScopeKey,
    routeCustomerSlug,
    routeAgentSlug,
    adminSession?.active_customer_slug,
    adminSession?.active_agent_slug,
  );

  useEffect(() => {
    switchAdminScopeRef.current = switchAdminScope;
  }, [switchAdminScope]);

  useEffect(() => {
    setScopeSwitchError('');
    setConfirmedRouteScopeKey('');
    scopeSwitchInFlightRef.current = false;
    setScopeSwitching(false);
    if (hasRouteScope) {
      setAdminDataLoaded(false);
      setAdminDataLoading(false);
      setAdminDataLoadError('');
      setBehaviorText('');
      setBehaviorMode('custom');
      setSetupBlueprintBehavior('');
      setSetupBlueprintRevision(null);
      setBlueprintCopyMessage('');
      setOnboardingMemoryText('');
      setVocabWords([]);
      setPreview(null);
      setCorePromptDraft('');
      setCallToActionsDraft('');
      setDocuments([]);
      setDocChunks([]);
      setExpandedChunk(null);
      setDeepScrapeRuns([]);
      setDeepScrapeMessage('');
      setDeepScrapeExpanded(false);
      setConversations([]);
      setSelectedDoc(null);
    }
  }, [hasRouteScope, routeScopeKey]);

  useEffect(() => {
    if (
      !isAuthenticated
      || !hasRouteScope
      || !routeScopeKey
      || routeScopeReady
      || scopeSwitchInFlightRef.current
      || scopeSwitchError
    ) {
      return;
    }

    let cancelled = false;
    scopeSwitchInFlightRef.current = true;
    setScopeSwitching(true);
    setScopeSwitchError('');
    switchAdminScopeRef.current(routeCustomerSlug, routeAgentSlug)
      .then((ok) => {
        if (!ok && !cancelled) {
          setScopeSwitchError('Admin-Kontext konnte nicht geladen werden.');
          return;
        }
        if (ok && !cancelled) {
          setConfirmedRouteScopeKey(routeScopeKey);
        }
      })
      .finally(() => {
        scopeSwitchInFlightRef.current = false;
        if (!cancelled) {
          setScopeSwitching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    hasRouteScope,
    isAuthenticated,
    routeAgentSlug,
    routeCustomerSlug,
    routeScopeKey,
    routeScopeReady,
    scopeSwitchError,
  ]);

  // Load data on auth
  const loadData = useCallback(async () => {
    if (superadminOnly || !isAuthenticated || !routeScopeReady) return;

    const loadWithRetry = async <T,>(label: string, loader: () => Promise<T | null>): Promise<T> => {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt > 0) {
          await wait(300 * attempt);
        }

        const result = await loader();
        if (result) {
          return result;
        }
      }

      throw new Error(`${label} konnte nicht geladen werden.`);
    };

    setAdminDataLoading(true);
    setAdminDataLoadError('');
    try {
      await loadWithRetry('Admin-Kontext', getAdminContext);
      const b = await loadWithRetry('Behavior Modifier', getBehavior);
      const om = await loadWithRetry('Onboarding', getOnboardingMemory);
      const v = await loadWithRetry('Vocabulary', getVocabulary);
      const p = await loadWithRetry('Admin-Profil', getAdminProfile);

      setBehaviorText(b.modifier_text || '');
      setBehaviorMode(b.behavior_mode === 'blueprint' ? 'blueprint' : 'custom');
      setSetupBlueprintBehavior(b.setup_blueprint?.behavior_text || '');
      setSetupBlueprintRevision(b.setup_blueprint?.revision || null);
      setOnboardingMemoryText(om.memory_text || '');
      setVocabWords(v.words || []);
      setVocabUpdatedAt(v.updated_at || null);
      setVocabNotice('');
      setAdminDisplayName(p.display_name);
      setAdminEmail(p.email || '');
      setAdminDataLoaded(true);
    } catch (loadError: any) {
      setAdminDataLoaded(false);
      setAdminDataLoadError(`${loadError?.message || 'Admin-Daten konnten nicht geladen werden.'} ${ADMIN_DATA_LOCKED_MESSAGE}`);
    } finally {
      setAdminDataLoading(false);
    }
  }, [isAuthenticated, routeScopeReady, getBehavior, getOnboardingMemory, getVocabulary, getAdminProfile, getAdminContext, superadminOnly]);

  useEffect(() => {
    if (isAuthenticated && !superadminOnly) loadData();
  }, [isAuthenticated, loadData, superadminOnly]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(loginUser, loginPass);
  };

  const handleSaveBehavior = async () => {
    if (!adminDataLoaded) {
      setAdminDataLoadError(ADMIN_DATA_LOCKED_MESSAGE);
      return;
    }
    const ok = await saveBehavior(behaviorText, 'custom');
    if (ok) {
      setBehaviorMode('custom');
      setBehaviorSaved(true);
      setTimeout(() => setBehaviorSaved(false), 2000);
    }
  };

  const handleToggleBehaviorMode = async () => {
    if (!adminDataLoaded || !setupBlueprintBehavior) return;
    const nextMode = behaviorMode === 'blueprint' ? 'custom' : 'blueprint';
    const ok = await saveBehavior(behaviorText, nextMode);
    if (ok) {
      setBehaviorMode(nextMode);
      setBehaviorSaved(true);
      setTimeout(() => setBehaviorSaved(false), 2000);
    }
  };

  const handleCopyBlueprint = async () => {
    if (!setupBlueprintBehavior) return;
    try {
      await navigator.clipboard.writeText(setupBlueprintBehavior);
      setBlueprintCopyMessage('In die Zwischenablage kopiert. Das Behaviour-Feld wurde nicht verändert.');
    } catch {
      setBlueprintCopyMessage('Kopieren war nicht möglich. Bitte Browser-Berechtigung für die Zwischenablage prüfen.');
    }
    setTimeout(() => setBlueprintCopyMessage(''), 3500);
  };

  const handleSaveOnboardingMemory = async () => {
    if (!adminDataLoaded) {
      setAdminDataLoadError(ADMIN_DATA_LOCKED_MESSAGE);
      return;
    }
    const ok = await saveOnboardingMemory(onboardingMemoryText);
    if (ok) {
      setOnboardingMemorySaved(true);
      setTimeout(() => setOnboardingMemorySaved(false), 2000);
    }
  };

  const handleSaveAdminProfile = async () => {
    if (!adminDataLoaded) {
      setAdminDataLoadError(ADMIN_DATA_LOCKED_MESSAGE);
      return;
    }
    const ok = await saveAdminProfile(adminDisplayName, adminEmail || null);
    if (ok) {
      setAdminProfileSaved(true);
      setTimeout(() => setAdminProfileSaved(false), 2000);
    }
  };

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    const users = await getUsers();
    if (users) setManagedUsers(users);
    setUsersLoading(false);
  }, [getUsers]);

  const currentManagedUser = managedUsers.find((user) => user.username === username) || null;
  const currentRole = adminSession?.role || currentManagedUser?.role || 'admin';
  const isSuperadmin = Boolean(adminSession?.is_superadmin) || currentRole === 'superadmin' || currentRole === 'owner';
  const canCreateAdmins = isSuperadmin;
  const canChangeRoles = isSuperadmin;
  const activeCustomerLabel = adminSession?.active_customer_name || adminSession?.active_customer_slug || 'Synaworks';
  const activeWorkspaceLabel = adminSession?.active_workspace_name || adminSession?.active_workspace_slug || 'main';
  const activeAgentLabel = adminSession?.active_agent_name || adminSession?.active_agent_slug || 'sally';
  const activeDashboardSlug = normalizeRouteSlug(
    adminSession?.active_customer_slug || routeCustomerSlug || 'synaworks',
    'synaworks',
  );
  const activeDashboardUrl = `/dashboard/${activeDashboardSlug}`;

  const showUserActionMessage = (message: string) => {
    setUserActionMessage(message);
    window.setTimeout(() => setUserActionMessage(''), 2500);
  };

  const handleCreateUser = async () => {
    if (!createUsername.trim() || createPassword.length < 8) return;
    const created = await createUser({
      username: createUsername.trim(),
      password: createPassword,
      email: createEmail.trim() || null,
      role: createRole,
      is_verified: createVerified,
    });
    if (!created) return;
    setCreateUsername('');
    setCreateEmail('');
    setCreatePassword('');
    setCreateRole('user');
    setCreateVerified(true);
    setIsCreateUserOpen(false);
    showUserActionMessage(`User ${created.username} angelegt.`);
    await loadUsers();
  };

  const handleVerifyManagedUser = async (userId: string) => {
    const updated = await verifyUser(userId);
    if (!updated) return;
    showUserActionMessage(`User ${updated.username} verifiziert.`);
    await loadUsers();
  };

  const handleResetManagedUserPassword = async (userId: string) => {
    if (!passwordResetValue.trim()) return;
    const updated = await resetUserPassword(userId, passwordResetValue);
    if (!updated) return;
    setPasswordResetUserId(null);
    setPasswordResetValue('');
    showUserActionMessage(`Passwort für ${updated.username} zurückgesetzt.`);
    await loadUsers();
  };

  const handleChangeManagedUserRole = async (userId: string, role: 'user' | 'admin' | 'superadmin') => {
    const updated = await updateUserRole(userId, role);
    if (!updated) return;
    showUserActionMessage(`Rolle von ${updated.username} auf ${updated.role} geändert.`);
    await loadUsers();
  };

  const handleUpdateManagedUserEmail = async (userId: string) => {
    const updated = await updateUserEmail(userId, emailEditValue.trim() || null);
    if (!updated) return;
    setEmailEditUserId(null);
    setEmailEditValue('');
    showUserActionMessage(`E-Mail von ${updated.username} aktualisiert.`);
    await loadUsers();
  };

  const handleDeleteManagedUser = async (userId: string, usernameToDelete: string) => {
    const deleted = await deleteUser(userId);
    if (!deleted) return;
    setDeleteConfirmUserId(null);
    showUserActionMessage(`User ${usernameToDelete} gelöscht.`);
    await loadUsers();
  };

  const loadSuperadminOverview = useCallback(async () => {
    if (!isSuperadmin) return;
    setSuperadminOverviewLoading(true);
    const overview = await getSuperadminOverview();
    if (overview) setSuperadminOverview(overview);
    setSuperadminOverviewLoading(false);
  }, [getSuperadminOverview, isSuperadmin]);

  const loadSuperadminUsers = useCallback(async () => {
    if (!isSuperadmin) return;
    setSuperadminUsersLoading(true);
    const payload = await getSuperadminUsers();
    if (payload) setSuperadminUsers(payload);
    setSuperadminUsersLoading(false);
  }, [getSuperadminUsers, isSuperadmin]);

  const handleDownloadUsageCsv = async () => {
    setUsageCsvDownloading(true);
    setUsageCsvMessage('');
    const ok = await downloadSuperadminUsageCsv();
    setUsageCsvDownloading(false);
    if (ok) {
      setUsageCsvMessage('CSV exportiert.');
      window.setTimeout(() => setUsageCsvMessage(''), 2500);
    }
  };

  useEffect(() => {
    if (isAuthenticated && routeScopeReady && !superadminOnly) loadUsers();
  }, [isAuthenticated, loadUsers, routeScopeReady, superadminOnly]);

  useEffect(() => {
    if (
      isAuthenticated
      && isSuperadmin
      && !superadminOverview
      && ((superadminOnly && superadminPanelTab === 'overview') || (!superadminOnly && activeTab === 'overview'))
    ) {
      loadSuperadminOverview();
    }
  }, [activeTab, isAuthenticated, isSuperadmin, loadSuperadminOverview, superadminOnly, superadminOverview, superadminPanelTab]);

  useEffect(() => {
    if (isAuthenticated && superadminOnly && superadminPanelTab === 'users' && isSuperadmin && !superadminUsers) {
      loadSuperadminUsers();
    }
  }, [isAuthenticated, isSuperadmin, loadSuperadminUsers, superadminOnly, superadminPanelTab, superadminUsers]);

  useEffect(() => {
    if (!passwordResetUserId) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById(`password-reset-${passwordResetUserId}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, [passwordResetUserId]);

  const flashVocabSaved = () => {
    setVocabSaved(true);
    setTimeout(() => setVocabSaved(false), 2000);
  };

  const persistVocabulary = useCallback(async (nextWords: VocabWord[]): Promise<boolean> => {
    if (!adminDataLoaded) {
      setAdminDataLoadError(ADMIN_DATA_LOCKED_MESSAGE);
      return false;
    }
    const saved = await saveVocabulary(nextWords, vocabUpdatedAt);
    if (!saved) {
      const latest = await getVocabulary();
      if (latest) {
        setVocabWords(latest.words || []);
        setVocabUpdatedAt(latest.updated_at || null);
        setVocabNotice('Eine andere Admin-Sitzung hat das gemeinsame Vocabulary geändert. Die aktuelle Version wurde geladen; bitte Änderung erneut eingeben.');
      }
      return false;
    }
    setVocabWords(saved.words || nextWords);
    setVocabUpdatedAt(saved.updated_at || null);
    setVocabNotice('');
    flashVocabSaved();
    return true;
  }, [adminDataLoaded, getVocabulary, saveVocabulary, vocabUpdatedAt]);

  const handleAddWord = async () => {
    if (!newWord.trim()) return;
    const inputAliases = newInputAliases
      .split(',')
      .map(alias => alias.trim())
      .filter(Boolean);
    const outputPronunciation = newPronunciation.trim();
    let updated: VocabWord[];
    if (editingIndex !== null) {
      updated = [...vocabWords];
      updated[editingIndex] = {
        word: newWord.trim(),
        context: newContext.trim(),
        pronunciation: outputPronunciation,
        output_pronunciation: outputPronunciation,
        input_aliases: inputAliases,
        voice_replacement: newVoiceReplacement.trim(),
      };
    } else {
      updated = [
        ...vocabWords,
        {
          word: newWord.trim(),
          context: newContext.trim(),
          pronunciation: outputPronunciation,
          output_pronunciation: outputPronunciation,
          input_aliases: inputAliases,
          voice_replacement: newVoiceReplacement.trim(),
        },
      ];
    }

    const saved = await persistVocabulary(updated);
    if (!saved) return;

    setEditingIndex(null);
    setNewWord('');
    setNewContext('');
    setNewPronunciation('');
    setNewInputAliases('');
    setNewVoiceReplacement('');
  };

  const handleEditWord = (index: number) => {
    const w = vocabWords[index];
    setNewWord(w.word);
    setNewContext(w.context);
    setNewPronunciation(w.output_pronunciation || w.pronunciation || '');
    setNewInputAliases((w.input_aliases || []).join(', '));
    setNewVoiceReplacement(w.voice_replacement || '');
    setEditingIndex(index);
  };

  const handleRemoveWord = async (index: number) => {
    const nextWords = vocabWords.filter((_, i) => i !== index);
    const saved = await persistVocabulary(nextWords);
    if (!saved) return;
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewWord('');
      setNewContext('');
      setNewPronunciation('');
      setNewInputAliases('');
      setNewVoiceReplacement('');
    }
  };

  const handleLoadPreview = useCallback(async (extended = isPromptPreviewExtended) => {
    const p = await getPromptPreview(extended);
    if (p) setPreview(p);
  }, [getPromptPreview, isPromptPreviewExtended]);

  const handleTogglePromptPreviewExtended = useCallback(async () => {
    const nextExtended = !isPromptPreviewExtended;
    setIsPromptPreviewExtended(nextExtended);
    const p = await getPromptPreview(nextExtended);
    if (p) setPreview(p);
  }, [getPromptPreview, isPromptPreviewExtended]);

  useEffect(() => {
    if (!preview || corePromptUnlocked) return;
    const coreSection = preview.sections.find(section => section.label === 'Core Prompt');
    if (coreSection) {
      setCorePromptDraft(coreSection.raw_content || coreSection.content || '');
    }
  }, [preview, corePromptUnlocked]);

  useEffect(() => {
    if (!preview || callToActionsUnlocked) return;
    const ctaSection = preview.sections.find(isCallToActionsSection);
    if (ctaSection) {
      setCallToActionsDraft(ctaSection.raw_content || '[]\n');
    }
  }, [preview, callToActionsUnlocked]);

  const handleUnlockCorePrompt = () => {
    if (!preview) return;
    const coreSection = preview.sections.find(section => section.label === 'Core Prompt');
    const confirmed = window.confirm(
      [
        `Der Core Prompt ist die Basis-Anweisung für ${activeAgentLabel} und kann Production-Verhalten direkt verändern.`,
        '',
        'Vor dieser Änderung wurde ein Server-Backup erstellt. Trotzdem sollte diese Datei nur bewusst geändert werden.',
        '',
        'Core Prompt jetzt entsperren und bearbeiten?',
      ].join('\n'),
    );
    if (!confirmed) return;
    setCorePromptDraft(coreSection?.raw_content || coreSection?.content || '');
    setCorePromptUnlocked(true);
    setCorePromptMessage('Core Prompt entsperrt. Änderungen sind erst nach Speichern aktiv.');
  };

  const handleCancelCorePromptEdit = () => {
    const coreSection = preview?.sections.find(section => section.label === 'Core Prompt');
    setCorePromptDraft(coreSection?.raw_content || coreSection?.content || '');
    setCorePromptUnlocked(false);
    setCorePromptMessage('');
  };

  const handleSaveCorePrompt = async () => {
    if (!preview) return;
    if (!corePromptDraft.trim()) {
      setCorePromptMessage('Core Prompt darf nicht leer sein.');
      return;
    }
    const confirmed = window.confirm(
      [
        `Core Prompt für Persona "${preview.persona}" wirklich speichern?`,
        '',
        'Diese Änderung wird serverseitig in die System-Prompt-Datei geschrieben und kann Production beeinflussen.',
      ].join('\n'),
    );
    if (!confirmed) return;
    setCorePromptSaving(true);
    const ok = await saveCorePrompt(preview.persona, corePromptDraft);
    setCorePromptSaving(false);
    if (!ok) {
      setCorePromptMessage('Core Prompt konnte nicht gespeichert werden.');
      return;
    }
    setCorePromptUnlocked(false);
    setCorePromptMessage('Core Prompt wurde gespeichert.');
    await handleLoadPreview(isPromptPreviewExtended);
  };

  const handleRefreshCallToActions = async () => {
    const data = await getCallToActions();
    if (!data) return;
    setCallToActionsDraft(data.content);
    setCallToActionsMessage(`${data.count.toLocaleString()} Call to Actions geladen.`);
  };

  const handleUnlockCallToActions = () => {
    if (!preview) return;
    const ctaSection = preview.sections.find(isCallToActionsSection);
    const confirmed = window.confirm(
      [
        `Call to Actions steuern, welche Links ${activeAgentLabel} im Gespräch per show_link anzeigen darf.`,
        '',
        'Änderungen können Production-Verhalten direkt verändern.',
        '',
        'Call to Actions jetzt entsperren und bearbeiten?',
      ].join('\n'),
    );
    if (!confirmed) return;
    setCallToActionsDraft(ctaSection?.raw_content || '[]\n');
    setCallToActionsUnlocked(true);
    setCallToActionsMessage('Call to Actions entsperrt. Änderungen sind erst nach Speichern aktiv.');
  };

  const handleCancelCallToActionsEdit = () => {
    const ctaSection = preview?.sections.find(isCallToActionsSection);
    setCallToActionsDraft(ctaSection?.raw_content || '[]\n');
    setCallToActionsUnlocked(false);
    setCallToActionsMessage('');
  };

  const parseCallToActionsDraft = (): unknown[] | null => {
    try {
      const parsed = JSON.parse(callToActionsDraft);
      if (!Array.isArray(parsed)) {
        setCallToActionsMessage('Call to Actions müssen ein JSON-Array sein.');
        return null;
      }
      return parsed;
    } catch (e: any) {
      setCallToActionsMessage(`JSON konnte nicht gelesen werden: ${e.message}`);
      return null;
    }
  };

  const handleFormatCallToActions = () => {
    const parsed = parseCallToActionsDraft();
    if (!parsed) return;
    setCallToActionsDraft(JSON.stringify(parsed, null, 2) + '\n');
    setCallToActionsMessage('JSON formatiert. Noch nicht gespeichert.');
  };

  const handleSaveCallToActions = async () => {
    const parsed = parseCallToActionsDraft();
    if (!parsed) return;
    const confirmed = window.confirm(
      [
        `${parsed.length.toLocaleString()} Call to Actions wirklich speichern?`,
        '',
        'Diese Änderung wird serverseitig im aktiven Customer-/Agent-Pfad gespeichert und beeinflusst Prompt Preview sowie show_link.',
      ].join('\n'),
    );
    if (!confirmed) return;
    setCallToActionsSaving(true);
    const saved = await saveCallToActions(callToActionsDraft);
    setCallToActionsSaving(false);
    if (!saved) {
      setCallToActionsMessage('Call to Actions konnten nicht gespeichert werden.');
      return;
    }
    setCallToActionsDraft(saved.content);
    setCallToActionsUnlocked(false);
    setCallToActionsMessage(`${saved.count.toLocaleString()} Call to Actions wurden gespeichert.`);
    await handleLoadPreview(isPromptPreviewExtended);
  };

  // Conversations handler
  const loadConversations = useCallback(async (query?: string) => {
    setConvoLoading(true);
    setConvoSearchActive((query ?? '').trim());
    const result = await getConversations(query);
    if (result) setConversations(result);
    setConvoLoading(false);
  }, [getConversations]);

  // Debounced search: reload conversations when the search term changes
  useEffect(() => {
    if (activeTab !== 'conversations') return;
    if (convoSearch.trim() === convoSearchActive) return;
    const handle = setTimeout(() => {
      loadConversations(convoSearch);
    }, 300);
    return () => clearTimeout(handle);
  }, [convoSearch, convoSearchActive, activeTab, loadConversations]);

  const handleDeleteConversationFromList = useCallback(async (chatId: string) => {
    const confirmed = window.confirm('Delete this conversation permanently?');
    if (!confirmed) return;
    const ok = await deleteConversation(chatId);
    if (ok) {
      setConversations(prev => prev.filter(convo => convo.chat_id !== chatId));
    }
  }, [deleteConversation]);

  // Knowledge base handlers
  const loadKnowledgeSummary = useCallback(async () => {
    const result = await getKnowledgeSummary();
    if (!result) return;
    const text = result.summary_text || '';
    setKnowledgeSummaryDraft(text);
    setKnowledgeSummarySavedText(text);
    setKnowledgeSummaryBaseText(result.base_text || '');
    setKnowledgeSummaryMetadata(knowledgeSummaryMetadataFrom(result));
  }, [getKnowledgeSummary]);

  const loadDeepScrapes = useCallback(async () => {
    const result = await getDeepScrapes();
    if (!result) return;
    setDeepScrapeRuns(result.runs);
  }, [getDeepScrapes]);

  const loadDocuments = useCallback(async () => {
    setKbLoading(true);
    const [result] = await Promise.all([getDocuments(), loadKnowledgeSummary(), loadDeepScrapes()]);
    if (result) {
      setDocuments(result.documents);
      setTotalChunks(result.total_chunks);
    }
    setKbLoading(false);
  }, [getDocuments, loadKnowledgeSummary, loadDeepScrapes]);

  const handleStartDeepScrape = useCallback(async () => {
    if (!deepScrapeUrl.trim()) {
      setDeepScrapeMessage('Bitte eine Website-URL eingeben.');
      return;
    }
    setDeepScrapeLoading(true);
    setDeepScrapeMessage('Der Deepscrape wird sicher geprüft und eingeplant…');
    const run = await startDeepScrape({
      url: deepScrapeUrl.trim(),
      max_pages: deepScrapeMaxPages,
      max_depth: deepScrapeMaxDepth,
      max_duration_seconds: 600,
      include_pdfs: deepScrapeIncludePdfs,
      allowed_subdomains: splitDeepScrapeList(deepScrapeSubdomains),
      include_patterns: splitDeepScrapeList(deepScrapeIncludePatterns),
      exclude_patterns: splitDeepScrapeList(deepScrapeExcludePatterns),
    });
    setDeepScrapeLoading(false);
    if (!run) {
      setDeepScrapeMessage('Der Deepscrape konnte nicht gestartet werden.');
      return;
    }
    setDeepScrapeRuns(previous => [run, ...previous.filter(item => item.id !== run.id)]);
    setDeepScrapeMessage(`Deepscrape für ${activeAgentLabel} wurde eingeplant. Die bestehende Knowledgebase bleibt bis zur vollständigen Aktivierung unverändert.`);
  }, [
    activeAgentLabel,
    deepScrapeExcludePatterns,
    deepScrapeIncludePatterns,
    deepScrapeIncludePdfs,
    deepScrapeMaxDepth,
    deepScrapeMaxPages,
    deepScrapeSubdomains,
    deepScrapeUrl,
    startDeepScrape,
  ]);

  const handleCancelDeepScrape = useCallback(async (runId: string) => {
    const ok = await cancelDeepScrape(runId);
    if (ok) {
      setDeepScrapeMessage('Abbruch angefordert. Bereits aktive Inhalte bleiben erhalten.');
      await loadDeepScrapes();
    }
  }, [cancelDeepScrape, loadDeepScrapes]);

  const handleActivateDeepScrape = useCallback(async (runId: string) => {
    const confirmed = window.confirm('Diese ältere Website-Version wieder aktivieren? Nur dieser Website-Import wird umgeschaltet; Uploads bleiben erhalten.');
    if (!confirmed) return;
    const ok = await activateDeepScrapeRun(runId);
    if (ok) {
      setDeepScrapeMessage('Die ausgewählte Website-Version wurde aktiviert.');
      await Promise.all([loadDeepScrapes(), loadDocuments()]);
    }
  }, [activateDeepScrapeRun, loadDeepScrapes, loadDocuments]);

  useEffect(() => {
    if (activeTab !== 'knowledgebase' || !deepScrapeRuns.some(run => ['queued', 'running'].includes(run.status))) {
      return undefined;
    }
    const interval = window.setInterval(async () => {
      const before = new Set(deepScrapeRuns.filter(run => ['queued', 'running'].includes(run.status)).map(run => run.id));
      const result = await getDeepScrapes();
      if (!result) return;
      setDeepScrapeRuns(result.runs);
      const finishedRun = result.runs.find(run => before.has(run.id) && !['queued', 'running'].includes(run.status));
      if (finishedRun) {
        setDeepScrapeMessage(
          ['succeeded', 'partial'].includes(finishedRun.status)
            ? 'Der Website-Import wurde aktiviert. Die neue Knowledgebase-Version ist verfügbar.'
            : finishedRun.status === 'cancelled'
              ? 'Der Deepscrape wurde abgebrochen; die bisherige Knowledgebase blieb aktiv.'
              : `Der Deepscrape ist fehlgeschlagen: ${finishedRun.error_message || 'Bitte Laufdetails prüfen.'}`
        );
        await loadDocuments();
      }
    }, 3000);
    return () => window.clearInterval(interval);
  }, [activeTab, deepScrapeRuns, getDeepScrapes, loadDocuments]);

  const handleUploadFile = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const supported = ['txt', 'md', 'pdf', 'docx', 'odt', 'mp3', 'wav', 'm4a', 'mp4', 'avi', 'mov', 'zip'];
    if (!supported.includes(ext)) {
      setUploadStatus({ type: 'error', message: `Unsupported file type: .${ext}` });
      return;
    }
    setUploadStatus({ type: 'uploading', message: `Uploading "${file.name}"… this may take a moment.` });
    const result = await uploadDocument(file);
    if (result) {
      setUploadStatus({ type: 'success', message: `"${result.document_name}" ingested — ${result.chunks_created} chunk${result.chunks_created !== 1 ? 's' : ''} created.` });
      // Refresh document list
      loadDocuments();
    } else {
      setUploadStatus({ type: 'error', message: 'Upload failed. Check the server log for details.' });
    }
  }, [uploadDocument, loadDocuments]);

  const handleSelectDoc = async (docName: string) => {
    if (selectedDoc === docName) {
      setSelectedDoc(null);
      setDocChunks([]);
      setExpandedChunk(null);
      return;
    }
    setSelectedDoc(docName);
    setExpandedChunk(null);
    setKbLoading(true);
    const result = await getDocumentChunks(docName);
    if (result) setDocChunks(result.chunks);
    setKbLoading(false);
  };

  const handleExpandChunk = async (chunkId: string) => {
    if (expandedChunk?.chunk_id === chunkId) {
      setExpandedChunk(null);
      return;
    }
    const detail = await getChunkDetail(chunkId);
    if (detail) setExpandedChunk(detail);
  };

  const openKnowledgeBaseTab = useCallback(async () => {
    setActiveTab('knowledgebase');
    if (documents.length === 0) {
      await loadDocuments();
    } else {
      await Promise.all([
        !knowledgeSummaryBaseText ? loadKnowledgeSummary() : Promise.resolve(),
        loadDeepScrapes(),
      ]);
    }
  }, [documents.length, knowledgeSummaryBaseText, loadDeepScrapes, loadDocuments, loadKnowledgeSummary]);

  const handleGenerateKnowledgeSummary = useCallback(async () => {
    const confirmed = window.confirm(
      'Knowledge Summary aus der aktuellen Knowledgebase generieren?\n\nDer Text wird nur als Entwurf in das Feld geschrieben. Aktiv wird er erst nach Speichern.'
    );
    if (!confirmed) return;
    setKnowledgeSummaryGenerating(true);
    setKnowledgeSummaryMessage('Knowledge Summary wird generiert...');
    const result = await generateKnowledgeSummary();
    setKnowledgeSummaryGenerating(false);
    if (!result) {
      setKnowledgeSummaryMessage('Knowledge Summary konnte nicht generiert werden.');
      return;
    }
    setKnowledgeSummaryDraft(result.summary_text || '');
    setKnowledgeSummaryMetadata(knowledgeSummaryMetadataFrom(result));
    setKnowledgeSummaryMessage('Entwurf generiert. Speichern aktiviert ihn im Prompt.');
  }, [generateKnowledgeSummary]);

  const handleSaveKnowledgeSummary = useCallback(async () => {
    setKnowledgeSummarySaving(true);
    const saved = await saveKnowledgeSummary(knowledgeSummaryDraft, knowledgeSummaryMetadata);
    setKnowledgeSummarySaving(false);
    if (!saved) {
      setKnowledgeSummaryMessage('Knowledge Summary konnte nicht gespeichert werden.');
      return;
    }
    const text = saved.summary_text || '';
    setKnowledgeSummaryDraft(text);
    setKnowledgeSummarySavedText(text);
    setKnowledgeSummaryMetadata(knowledgeSummaryMetadataFrom(saved));
    setKnowledgeSummaryMessage(text ? 'Knowledge Summary wurde gespeichert.' : 'Knowledge Summary wurde geleert und wird im Prompt weggelassen.');
  }, [knowledgeSummaryDraft, knowledgeSummaryMetadata, saveKnowledgeSummary]);

  const handleResetKnowledgeSummary = useCallback(() => {
    setKnowledgeSummaryDraft(knowledgeSummarySavedText);
    setKnowledgeSummaryMessage('');
  }, [knowledgeSummarySavedText]);

  useEffect(() => {
    if (superadminOnly || !isAuthenticated || !routeScopeReady) return;

    const requestedTab = searchParams.get('tab');
    const requestedChunkId = searchParams.get('chunk');
    const deepLinkKey = `${requestedTab || ''}:${requestedChunkId || ''}`;

    if (!requestedTab && !requestedChunkId) return;
    if (deepLinkHandledRef.current === deepLinkKey) return;

    const applyDeepLink = async () => {
      if (requestedTab === 'knowledgebase' || requestedChunkId) {
        await openKnowledgeBaseTab();
      } else if (requestedTab === 'conversations') {
        setActiveTab('conversations');
        if (conversations.length === 0) {
          await loadConversations();
        }
      } else if (requestedTab === 'preview') {
        setActiveTab('preview');
        await handleLoadPreview();
      } else if (requestedTab === 'users' || requestedTab === 'behavior' || requestedTab === 'vocabulary') {
        setActiveTab(requestedTab);
      }

      if (requestedChunkId) {
        const detail = await getChunkDetail(requestedChunkId);
        if (!detail) return;

        const chunkList = await getDocumentChunks(detail.document_name);
        if (!chunkList) return;

        setSelectedDoc(detail.document_name);
        setDocChunks(chunkList.chunks);
        setExpandedChunk(detail);
      }

      deepLinkHandledRef.current = deepLinkKey;
    };

    applyDeepLink();
  }, [
    isAuthenticated,
    searchParams,
    openKnowledgeBaseTab,
    conversations.length,
    loadConversations,
    handleLoadPreview,
    getChunkDetail,
    getDocumentChunks,
    routeScopeReady,
    superadminOnly,
  ]);

  const handleDeleteChunk = async (chunkId: string) => {
    const ok = await deleteChunk(chunkId);
    if (ok) {
      setDocChunks(prev => prev.filter(c => c.chunk_id !== chunkId));
      if (expandedChunk?.chunk_id === chunkId) setExpandedChunk(null);
      setConfirmDelete(null);
      // Update document list counts
      setDocuments(prev => prev.map(doc =>
        doc.document_name === selectedDoc
          ? { ...doc, chunk_count: doc.chunk_count - 1 }
          : doc
      ).filter(doc => doc.chunk_count > 0));
      setTotalChunks(prev => prev - 1);
    }
  };

  const handleDeleteDocument = async (docName: string) => {
    const ok = await deleteDocument(docName);
    if (ok) {
      setConfirmDelete(null);
      setSelectedDoc(null);
      setDocChunks([]);
      setExpandedChunk(null);
      // Remove from list
      const removed = documents.find(d => d.document_name === docName);
      setDocuments(prev => prev.filter(d => d.document_name !== docName));
      if (removed) setTotalChunks(prev => prev - removed.chunk_count);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.assign('/');
  };

  // ─── Login Screen ───────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${d ? 'bg-gray-950' : 'bg-gray-100'}`}>
        <div className={`w-full max-w-sm ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border rounded-lg shadow-lg`}>
          <div className={`px-6 py-4 border-b ${d ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${d ? 'bg-blue-400' : 'bg-blue-500'}`} />
              <h1 className={`text-sm font-semibold tracking-wide uppercase ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                {superadminOnly ? 'Superadmin Panel' : 'Admin Panel'}
              </h1>
            </div>
            <p className={`mt-1 text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
              Synaworks Agent Configuration
            </p>
          </div>
          <form onSubmit={handleLogin} className="p-6 space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Username</label>
              <input
                type="text"
                value={loginUser}
                onChange={e => setLoginUser(e.target.value)}
                className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                  ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                autoFocus
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Password</label>
              <input
                type="password"
                value={loginPass}
                onChange={e => setLoginPass(e.target.value)}
                className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                  ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
              />
            </div>
            {error && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded px-3 py-2">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 text-sm font-medium rounded transition-colors
                ${loading ? 'opacity-50 cursor-wait' : ''}
                ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Authenticated Panel ────────────────────────────────────────
  const tabs: { key: Tab; label: string }[] = [
    { key: 'behavior', label: 'Behavior' },
    { key: 'vocabulary', label: 'Vocabulary' },
    { key: 'knowledgebase', label: 'Knowledge Base' },
    { key: 'conversations', label: 'Conversations' },
    { key: 'preview', label: 'Prompt Preview' },
    { key: 'users', label: 'Users' },
  ];
  const visibleTabs = superadminOnly ? [] : tabs;

  return (
    <div className={`min-h-screen ${d ? 'bg-gray-950 text-gray-200' : 'bg-gray-100 text-gray-900'}`}>
      {scopeSwitching && (
        <div className={`fixed inset-x-0 top-0 z-50 px-4 py-2 text-center text-xs font-medium ${d ? 'bg-blue-950 text-blue-100' : 'bg-blue-50 text-blue-700'}`}>
          Admin-Kontext wird geladen...
        </div>
      )}
      {scopeSwitchError && (
        <div className={`fixed inset-x-0 top-0 z-50 px-4 py-2 text-center text-xs font-medium ${d ? 'bg-red-950 text-red-100' : 'bg-red-50 text-red-700'}`}>
          {scopeSwitchError}
        </div>
      )}
      {isAuthenticated && hasRouteScope && !routeScopeReady && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 backdrop-blur-[1px]" role="status" aria-live="polite">
          <div className={`rounded-lg border px-5 py-4 text-sm font-semibold shadow-xl ${d ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-800'}`}>
            {scopeSwitchError || `Agent-Kontext ${routeAgentSlug} wird sicher geladen…`}
          </div>
        </div>
      )}
      {userActionMessage && (
        <div
          role="status"
          className={`fixed right-4 top-4 z-50 max-w-[calc(100vw-2rem)] rounded-md border px-4 py-3 text-sm shadow-lg ${
            d
              ? 'border-emerald-500/30 bg-emerald-950 text-emerald-100'
              : 'border-emerald-200 bg-white text-emerald-700'
          }`}
        >
          {userActionMessage}
        </div>
      )}
      {/* Header */}
      <header className={`border-b ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className={`${superadminOnly ? 'max-w-[1600px]' : 'max-w-6xl'} mx-auto px-4 sm:px-6 py-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between`}>
          <button
            onClick={() => navigate(activeDashboardUrl)}
            className="flex items-center gap-2 rounded-lg p-1 -m-1 hover:opacity-70 transition-opacity"
            title="Zum Dashboard"
          >
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
              <img src="/favicon-synaworks.png" alt="Synaworks" className="w-5 h-5 object-contain" />
            </div>
            <span className={`text-sm font-semibold ${d ? 'text-gray-300' : 'text-gray-700'}`}>Synaworks.ai</span>
          </button>
          <div className="flex flex-col gap-2 lg:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded border px-2 py-1 text-xs ${d ? 'border-gray-700 bg-gray-950/50 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                {currentRole}
              </span>
              <span className={`rounded border px-2 py-1 text-xs ${d ? 'border-gray-700 bg-gray-950/50 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                {activeCustomerLabel}
              </span>
              <span className={`rounded border px-2 py-1 text-xs ${d ? 'border-gray-700 bg-gray-950/50 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                {activeWorkspaceLabel} / {activeAgentLabel}
              </span>
            </div>
            <div className="flex items-center justify-end gap-4">
              <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{adminDisplayName} · {username}</span>
              <button
                onClick={() => navigate(activeDashboardUrl)}
                className={`inline-flex items-center gap-1.5 rounded border px-3 py-1 text-xs transition-colors
                  ${d ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Dashboard
              </button>
              {isSuperadmin && (
                <button
                  onClick={() => navigate('/debug')}
                  className={`text-xs px-3 py-1 rounded border transition-colors
                    ${d ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
                >
                  Debug
                </button>
              )}
              <button
                onClick={handleLogout}
                className={`text-xs px-3 py-1 rounded border transition-colors
                  ${d ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className={`${superadminOnly ? 'max-w-[1600px]' : 'max-w-6xl'} mx-auto px-4 sm:px-6 py-6`}>
        {superadminOnly && !isSuperadmin && (
          <div className={`rounded-lg border p-5 text-sm ${d ? 'border-red-900/50 bg-red-950/30 text-red-100' : 'border-red-200 bg-red-50 text-red-700'}`}>
            Superadmin-Rechte erforderlich.
          </div>
        )}

        {/* Tabs */}
        {superadminOnly && isSuperadmin && (
          <nav role="tablist" aria-label="Superadmin Bereiche" className={`mb-6 flex gap-1 overflow-x-auto rounded-lg border p-1 ${d ? 'border-gray-800 bg-gray-900' : 'border-slate-200 bg-white'}`}>
            {[
              { key: 'overview' as SuperadminPanelTab, label: 'Overview' },
              { key: 'users' as SuperadminPanelTab, label: 'Users' },
              { key: 'avatars' as SuperadminPanelTab, label: 'Avatars' },
              { key: 'avatar-lab' as SuperadminPanelTab, label: 'Avatar Lab' },
            ].map(t => (
              <button
                key={t.key}
                id={`superadmin-tab-${t.key}`}
                role="tab"
                type="button"
                aria-selected={superadminPanelTab === t.key}
                aria-controls={`superadmin-tabpanel-${t.key}`}
                onClick={() => {
                  setSuperadminPanelTab(t.key);
                  if (t.key === 'overview') loadSuperadminOverview();
                  if (t.key === 'users') loadSuperadminUsers();
                }}
                className={`min-h-[44px] rounded-md px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-inset
                  ${superadminPanelTab === t.key
                    ? 'bg-orange-500 text-white shadow-sm'
                    : (d ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                  }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        )}

        {!superadminOnly && (
        <nav className="flex gap-1 mb-6 overflow-x-auto pb-1">
          {visibleTabs.map(t => (
            <button
              key={t.key}
              onClick={() => {
                setActiveTab(t.key);
                if (t.key === 'preview') handleLoadPreview();
                if (t.key === 'knowledgebase') {
                  if (documents.length === 0) loadDocuments();
                  else loadDeepScrapes();
                }
                if (t.key === 'conversations' && conversations.length === 0) loadConversations();
              }}
              className={`px-4 py-2 text-sm rounded-md transition-colors
                ${activeTab === t.key
                  ? (d ? 'bg-gray-800 text-white' : 'bg-white text-gray-900 shadow-sm')
                  : (d ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        )}

        {adminDataLoading && (
          <div className={`mb-4 rounded px-3 py-2 text-xs ${d ? 'border border-blue-900/50 bg-blue-950/30 text-blue-100' : 'border border-blue-200 bg-blue-50 text-blue-700'}`}>
            Admin-Daten werden geladen...
          </div>
        )}

        {adminDataLoadError && (
          <div className={`mb-4 flex flex-col gap-3 rounded px-3 py-2 text-xs sm:flex-row sm:items-center sm:justify-between ${d ? 'border border-amber-900/60 bg-amber-950/30 text-amber-100' : 'border border-amber-200 bg-amber-50 text-amber-800'}`}>
            <span>{adminDataLoadError}</span>
            <button
              type="button"
              onClick={loadData}
              disabled={adminDataLoading}
              className={`shrink-0 rounded border px-3 py-1.5 font-semibold transition disabled:cursor-wait disabled:opacity-50 ${d ? 'border-amber-700 text-amber-100 hover:bg-amber-900/40' : 'border-amber-300 text-amber-900 hover:bg-amber-100'}`}
            >
              Erneut laden
            </button>
          </div>
        )}

        {error && (!superadminOnly || !['avatars', 'avatar-lab'].includes(superadminPanelTab)) && (
          <div className="mb-4 text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded px-3 py-2">
            {error}
          </div>
        )}

        {((!superadminOnly && activeTab === 'overview') || (superadminOnly && superadminPanelTab === 'overview')) && isSuperadmin && (
          <div id={superadminOnly ? 'superadmin-tabpanel-overview' : undefined} role={superadminOnly ? 'tabpanel' : undefined} aria-labelledby={superadminOnly ? 'superadmin-tab-overview' : undefined}>
            <div className="space-y-5">
              {superadminOverviewLoading && !superadminOverview && (
                <div className={`rounded-xl border p-8 text-sm ${d ? 'border-gray-800 bg-gray-900 text-gray-400' : 'border-slate-200 bg-white text-slate-600'}`} role="status">Kostenübersicht wird geladen…</div>
              )}
              {superadminOverview && (
                <SuperadminCostControlDashboard
                  overview={superadminOverview}
                  isDarkMode={d}
                  loading={superadminOverviewLoading}
                  csvDownloading={usageCsvDownloading}
                  csvMessage={usageCsvMessage}
                  onRefresh={loadSuperadminOverview}
                  onDownloadCsv={handleDownloadUsageCsv}
                />
              )}
            </div>
          </div>
        )}

        {superadminOnly && superadminPanelTab === 'users' && isSuperadmin && (
          <div id="superadmin-tabpanel-users" role="tabpanel" aria-labelledby="superadmin-tab-users" className={`rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className={`px-5 py-3 border-b ${d ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>Platform Users</h2>
                  <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    Provider-Sicht auf alle Accounts, Customer-Memberships und Agent-Zuordnungen.
                  </p>
                </div>
                <button
                  onClick={loadSuperadminUsers}
                  disabled={superadminUsersLoading}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${superadminUsersLoading ? 'opacity-50 cursor-wait' : ''} ${d ? 'border-gray-700 text-gray-300 hover:text-white hover:border-gray-500' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400'}`}
                >
                  Aktualisieren
                </button>
              </div>
            </div>
            <div className="p-5">
              {superadminUsersLoading && !superadminUsers ? (
                <div className={`text-sm ${d ? 'text-gray-400' : 'text-gray-600'}`}>Users werden geladen...</div>
              ) : null}
              {superadminUsers ? (
                <div className={`rounded-lg border overflow-hidden ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                  <div className="overflow-x-auto">
                    <table className="min-w-[1180px] w-full text-sm">
                      <thead>
                        <tr className={d ? 'bg-gray-950 border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>User</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Accounttyp</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Status</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Verifiziert</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Customers</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Agenten</th>
                          <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Letzter Login</th>
                        </tr>
                      </thead>
                      <tbody>
                        {superadminUsers.users.map(user => {
                          const customerLabels = user.memberships.length
                            ? user.memberships.map(membership => (
                                `${membership.customer_slug || membership.customer_id || '-'} (${membership.role}, ${membership.status})`
                              ))
                            : ['keine Membership'];
                          const agentLabels = user.agents.length
                            ? user.agents.map(agent => (
                                `${agent.customer_slug || '-'} / ${agent.workspace_slug || '-'} / ${agent.agent_name || agent.agent_slug || '-'}`
                              ))
                            : ['-'];

                          return (
                            <tr key={user.id} className={d ? 'border-b border-gray-800/60' : 'border-b border-gray-100'}>
                              <td className="px-4 py-3 align-top">
                                <div className={`font-medium ${d ? 'text-gray-200' : 'text-gray-800'}`}>{user.username}</div>
                                <div className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{user.email || 'keine E-Mail'}</div>
                                <div className={`text-[11px] ${d ? 'text-gray-600' : 'text-gray-400'}`}>{user.id}</div>
                              </td>
                              <td className={`px-4 py-3 align-top ${d ? 'text-gray-300' : 'text-gray-700'}`}>{formatAccountRole(user.role)}</td>
                              <td className={`px-4 py-3 align-top ${d ? 'text-gray-400' : 'text-gray-600'}`}>{user.status}</td>
                              <td className={`px-4 py-3 align-top ${d ? 'text-gray-400' : 'text-gray-600'}`}>{user.is_verified ? 'Ja' : 'Nein'}</td>
                              <td className={`px-4 py-3 align-top text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                                <div className="flex flex-col gap-1">
                                  {customerLabels.map((label, index) => <span key={`${user.id}-customer-${index}`}>{label}</span>)}
                                </div>
                              </td>
                              <td className={`px-4 py-3 align-top text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                                <div className="flex flex-col gap-1">
                                  {agentLabels.map((label, index) => <span key={`${user.id}-agent-${index}`}>{label}</span>)}
                                </div>
                              </td>
                              <td className={`px-4 py-3 align-top text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{formatDateTime(user.last_login_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {superadminOnly && superadminPanelTab === 'avatars' && isSuperadmin && (
          <div id="superadmin-tabpanel-avatars" role="tabpanel" aria-labelledby="superadmin-tab-avatars">
            <SuperadminAvatarManagement isDarkMode={d} />
          </div>
        )}

        {superadminOnly && superadminPanelTab === 'avatar-lab' && isSuperadmin && (
          <div id="superadmin-tabpanel-avatar-lab" role="tabpanel" aria-labelledby="superadmin-tab-avatar-lab">
            <SuperadminAvatarLab isDarkMode={d} />
          </div>
        )}

        {!superadminOnly && activeTab === 'users' && (
          <div className={`rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className={`px-5 py-3 border-b ${d ? 'border-gray-800' : 'border-gray-200'}`}>
              <h2 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>Users</h2>
              <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                Owner verwaltet Admins und Users. Admins sehen sich selbst und normale Users.
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div className={`rounded-lg border p-4 ${d ? 'border-gray-800 bg-gray-950/50' : 'border-gray-200 bg-gray-50/70'}`}>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Admin-Name</label>
                      <input
                        type="text"
                        value={adminDisplayName}
                        onChange={e => setAdminDisplayName(e.target.value)}
                        className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                          ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                        placeholder="z. B. Daniel"
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>E-Mail</label>
                      <input
                        type="email"
                        value={adminEmail}
                        onChange={e => setAdminEmail(e.target.value)}
                        className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                          ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                        placeholder="z. B. admin@firma.de"
                      />
                    </div>
                    <button
                      onClick={handleSaveAdminProfile}
                      disabled={loading || adminDataLoading || !adminDataLoaded}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors
                        ${loading || adminDataLoading || !adminDataLoaded ? 'opacity-50 cursor-wait' : ''}
                        ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                    >
                      {adminDataLoading ? 'Lädt...' : 'Speichern'}
                    </button>
                  </div>
                </div>
                {adminProfileSaved && (
                  <p className={`mt-2 text-xs ${d ? 'text-emerald-400' : 'text-emerald-600'}`}>Profil gespeichert.</p>
                )}
              </div>
              <div className={`rounded-lg border p-4 ${d ? 'border-gray-800 bg-gray-950/30' : 'border-gray-200 bg-gray-50/50'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>User anlegen</h3>
                    <p className={`mt-1 text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                      {canCreateAdmins ? 'Sekundäre Aktion für neue Users oder Admins.' : 'Sekundäre Aktion für neue Users.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsCreateUserOpen((current) => !current)}
                      className={`px-3 py-1.5 text-xs rounded border transition-colors ${d ? 'border-gray-700 text-gray-300 hover:text-white hover:border-gray-500' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400'}`}
                    >
                      {isCreateUserOpen ? 'Schließen' : 'Neu'}
                    </button>
                  </div>
                </div>
                {isCreateUserOpen && (
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                    <label className="block">
                      <span className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Username *</span>
                      <input
                        type="text"
                        value={createUsername}
                        onChange={e => setCreateUsername(e.target.value)}
                        placeholder="z. B. teamify-admin"
                        className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                      />
                    </label>
                    <label className="block">
                      <span className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>E-Mail</span>
                      <input
                        type="email"
                        value={createEmail}
                        onChange={e => setCreateEmail(e.target.value)}
                        placeholder="optional"
                        className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                      />
                    </label>
                    <label className="block">
                      <span className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Initiales Passwort *</span>
                      <input
                        type="password"
                        value={createPassword}
                        onChange={e => setCreatePassword(e.target.value)}
                        placeholder="Mindestens 8 Zeichen"
                        className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                      />
                    </label>
                    <label className="block">
                      <span className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Accounttyp</span>
                      <select
                        value={createRole}
                        onChange={e => setCreateRole(e.target.value as 'user' | 'admin' | 'superadmin')}
                        className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                      >
                        <option value="user">User</option>
                        {canCreateAdmins && <option value="admin">Plattform Admin</option>}
                        {canCreateAdmins && <option value="superadmin">Superadmin</option>}
                      </select>
                    </label>
                    <div className="flex items-end gap-3">
                      <label className={`flex items-center gap-2 text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                        <input
                          type="checkbox"
                          checked={createVerified}
                          onChange={e => setCreateVerified(e.target.checked)}
                        />
                        Direkt verifizieren
                      </label>
                      <button
                        onClick={handleCreateUser}
                        disabled={loading || !createUsername.trim() || createPassword.length < 8}
                        className={`px-4 py-2 text-sm font-medium rounded transition-colors ${loading ? 'opacity-50 cursor-wait' : ''} ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                      >
                        Anlegen
                      </button>
                    </div>
                  </div>
                )}
                {userActionMessage && (
                  <p className={`mt-3 text-xs ${d ? 'text-emerald-400' : 'text-emerald-600'}`}>{userActionMessage}</p>
                )}
              </div>
              <div className={`rounded-lg border overflow-hidden ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
                  <thead>
                    <tr className={d ? 'bg-gray-950 border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>User</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Rolle</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Status</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Verifiziert</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Registriert</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Letzter Login</th>
                      <th className={`px-4 py-2 text-right text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managedUsers.map((user) => {
                      const isSelf = user.username === username;
                      const canPromoteToAdmin = canChangeRoles && user.role === 'user';
                      const canDemoteToUser = canChangeRoles && (user.role === 'admin' || user.role === 'superadmin' || user.role === 'owner') && !isSelf;
                      const canResetPassword = user.role === 'user' || isSelf || isSuperadmin;
                      return (
                        <React.Fragment key={user.id}>
                        <tr className={d ? 'border-b border-gray-800/60' : 'border-b border-gray-100'}>
                          <td className="px-4 py-3 align-top">
                            <div className={`font-medium ${d ? 'text-gray-200' : 'text-gray-800'}`}>{user.username}</div>
                            <div className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{user.email || 'keine E-Mail'}</div>
                          </td>
                          <td className={`px-4 py-3 align-top ${d ? 'text-gray-300' : 'text-gray-700'}`}>{formatAccountRole(user.role)}</td>
                          <td className={`px-4 py-3 align-top ${d ? 'text-gray-400' : 'text-gray-600'}`}>{user.status}</td>
                          <td className={`px-4 py-3 align-top ${d ? 'text-gray-400' : 'text-gray-600'}`}>{user.is_verified ? 'Ja' : 'Nein'}</td>
                          <td className={`px-4 py-3 align-top text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{formatDateTime(user.created_at)}</td>
                          <td className={`px-4 py-3 align-top text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{formatDateTime(user.last_login_at)}</td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex flex-wrap justify-end gap-2">
                                {!user.is_verified && (
                                  <button
                                    onClick={() => handleVerifyManagedUser(user.id)}
                                    disabled={loading}
                                    className={`text-xs px-2.5 py-1 rounded transition-colors ${d ? 'bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                                  >
                                    Verifizieren
                                  </button>
                                )}
                                {canPromoteToAdmin && (
                                  <button
                                    onClick={() => handleChangeManagedUserRole(user.id, 'admin')}
                                    disabled={loading}
                                    className={`text-xs px-2.5 py-1 rounded transition-colors ${d ? 'bg-violet-900/40 text-violet-300 hover:bg-violet-900/60' : 'bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
                                  >
                                    Zu Admin
                                  </button>
                                )}
                                {canDemoteToUser && (
                                  <button
                                    onClick={() => handleChangeManagedUserRole(user.id, 'user')}
                                    disabled={loading}
                                    className={`text-xs px-2.5 py-1 rounded transition-colors ${d ? 'bg-amber-900/40 text-amber-300 hover:bg-amber-900/60' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                                  >
                                    Zu User
                                  </button>
                                )}
                                {canResetPassword && (
                                  <button
                                    onClick={() => {
                                      setPasswordResetUserId(passwordResetUserId === user.id ? null : user.id);
                                      setPasswordResetValue('');
                                    }}
                                    disabled={loading}
                                    className={`text-xs px-2.5 py-1 rounded transition-colors ${d ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                                  >
                                    Passwort
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setEmailEditUserId(emailEditUserId === user.id ? null : user.id);
                                    setEmailEditValue(user.email || '');
                                  }}
                                  disabled={loading}
                                  className={`text-xs px-2.5 py-1 rounded transition-colors ${d ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                >
                                  E-Mail
                                </button>
                                {!isSelf && (user.role === 'user' || isSuperadmin) && (
                                  <button
                                    onClick={() => setDeleteConfirmUserId(deleteConfirmUserId === user.id ? null : user.id)}
                                    disabled={loading}
                                    className={`text-xs px-2.5 py-1 rounded transition-colors ${d ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                                  >
                                    Löschen
                                  </button>
                                )}
                              </div>
                              {deleteConfirmUserId === user.id && (
                                <div className={`w-full max-w-sm rounded border p-3 text-left ${d ? 'border-red-900/40 bg-red-950/30' : 'border-red-200 bg-red-50'}`}>
                                  <p className={`text-xs ${d ? 'text-red-200' : 'text-red-700'}`}>
                                    Wirklich löschen? Dieser Schritt entfernt den User dauerhaft.
                                  </p>
                                  <div className="mt-2 flex justify-end gap-2">
                                    <button
                                      onClick={() => setDeleteConfirmUserId(null)}
                                      className={`px-3 py-1.5 text-xs rounded border ${d ? 'border-gray-700 text-gray-300 hover:text-white' : 'border-gray-300 text-gray-700 hover:text-black'}`}
                                    >
                                      Abbrechen
                                    </button>
                                    <button
                                      onClick={() => handleDeleteManagedUser(user.id, user.username)}
                                      disabled={loading}
                                      className={`px-3 py-1.5 text-xs font-medium rounded ${d ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-500'} ${loading ? 'opacity-50 cursor-wait' : ''}`}
                                    >
                                      Endgültig löschen
                                    </button>
                                  </div>
                                </div>
                              )}
                              {emailEditUserId === user.id && (
                                <div className="flex w-full max-w-sm items-center gap-2">
                                  <input
                                    type="email"
                                    value={emailEditValue}
                                    onChange={e => setEmailEditValue(e.target.value)}
                                    placeholder="Neue E-Mail oder leer"
                                    className={`flex-1 px-3 py-2 text-xs rounded border outline-none transition-colors ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                                  />
                                  <button
                                    onClick={() => handleUpdateManagedUserEmail(user.id)}
                                    disabled={loading}
                                    className={`px-3 py-2 text-xs font-medium rounded transition-colors ${loading ? 'opacity-50 cursor-wait' : ''} ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                                  >
                                    Speichern
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                        {passwordResetUserId === user.id && (
                          <tr id={`password-reset-${user.id}`} className={d ? 'border-b border-gray-800/60 bg-blue-950/10' : 'border-b border-gray-100 bg-blue-50/40'}>
                            <td colSpan={7} className="px-4 py-4">
                              <div className={`rounded border p-4 ${d ? 'border-blue-900/40 bg-gray-950/70' : 'border-blue-100 bg-white'}`}>
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                                  <div className="min-w-0 flex-1">
                                    <label className={`block text-xs font-medium mb-1.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {isSelf ? 'Neues eigenes Passwort' : `Neues Passwort für ${user.username}`}
                                    </label>
                                    <input
                                      type="password"
                                      value={passwordResetValue}
                                      onChange={e => setPasswordResetValue(e.target.value)}
                                      placeholder="Mindestens 8 Zeichen"
                                      className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
                                    />
                                  </div>
                                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                                    <button
                                      onClick={() => {
                                        setPasswordResetUserId(null);
                                        setPasswordResetValue('');
                                      }}
                                      className={`px-4 py-2 text-sm rounded border transition-colors ${d ? 'border-gray-700 text-gray-300 hover:text-white hover:border-gray-500' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400'}`}
                                    >
                                      Abbrechen
                                    </button>
                                    <button
                                      onClick={() => handleResetManagedUserPassword(user.id)}
                                      disabled={loading || !passwordResetValue.trim()}
                                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${loading || !passwordResetValue.trim() ? 'opacity-50 cursor-not-allowed' : ''} ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                                    >
                                      Speichern
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                    {!usersLoading && managedUsers.length === 0 && (
                      <tr>
                        <td colSpan={7} className={`px-4 py-8 text-center text-sm ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                          Keine User gefunden.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Behavior Tab ─── */}
        {!superadminOnly && activeTab === 'behavior' && (
          <div className={`rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className={`px-5 py-3 border-b ${d ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>Behaviour Prompt</h2>
                  <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    Bestimmt das Verhalten von {activeAgentLabel}. Wähle die vorbereitete Kunden-Vorlage oder deinen eigenen gespeicherten Entwurf.
                  </p>
                </div>
                <a
                  href="/admin/onboarding"
                  className={`shrink-0 px-3 py-1.5 text-xs rounded border transition-colors ${d ? 'border-gray-700 text-gray-300 hover:text-white hover:border-gray-500' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400'}`}
                >
                  Onboarding öffnen
                </a>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className={`rounded-lg border px-4 py-3 ${
                behaviorMode === 'custom'
                  ? d ? 'border-emerald-500/35 bg-emerald-950/30 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : d ? 'border-amber-500/35 bg-amber-950/30 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}>
                <p className="text-sm font-semibold">
                  Aktiv: {behaviorMode === 'custom'
                    ? `eigener Behaviour Prompt für ${activeAgentLabel}`
                    : `geerbter Kunden-Blueprint${setupBlueprintRevision ? ` · Version ${setupBlueprintRevision}` : ''}`}
                </p>
                <p className="mt-1 text-xs opacity-80">
                  „Speichern“ aktiviert einen manuell bearbeiteten Prompt automatisch als eigenen Prompt dieses Avatars.
                </p>
              </div>
              {setupBlueprintBehavior && (
                <details className={`rounded border px-3 py-2 ${d ? 'border-gray-800 bg-gray-950/30' : 'border-gray-200 bg-gray-50'}`}>
                  <summary className={`cursor-pointer text-xs font-medium ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                    Geerbter Kunden-Blueprint{setupBlueprintRevision ? ` · Version ${setupBlueprintRevision}` : ''}
                  </summary>
                  <p className={`mt-2 text-xs ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    Diese vorbereitete Fassung stammt aus dem Setup Wizard. Sie ist kein zusätzlicher Prompt, sondern kann als Behaviour Modifier ausgewählt werden.
                  </p>
                  <pre className={`mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded p-3 text-xs ${d ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-600'}`}>
                    {setupBlueprintBehavior}
                  </pre>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleToggleBehaviorMode}
                      disabled={loading || adminDataLoading || !adminDataLoaded}
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${behaviorMode === 'blueprint' ? (d ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300') : 'bg-blue-600 text-white hover:bg-blue-500'}`}
                    >
                      {behaviorMode === 'blueprint'
                        ? 'Stattdessen eigenen Behaviour Prompt von unten verwenden'
                        : 'Als Behaviour verwenden'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyBlueprint}
                      className={`rounded border px-3 py-1.5 text-xs transition-colors ${d ? 'border-gray-700 text-gray-300 hover:border-gray-500' : 'border-gray-300 text-gray-700 hover:border-gray-500'}`}
                    >
                      Text kopieren
                    </button>
                    <span className={`text-xs font-medium ${behaviorMode === 'blueprint' ? 'text-green-500' : (d ? 'text-gray-500' : 'text-gray-500')}`}>
                      {behaviorMode === 'blueprint' ? 'Vorlage ist aktiv' : 'Eigener Behaviour Prompt ist aktiv'}
                    </span>
                  </div>
                  {blueprintCopyMessage && (
                    <p className={`mt-2 text-xs ${blueprintCopyMessage.startsWith('In die') ? 'text-green-500' : 'text-red-500'}`}>
                      {blueprintCopyMessage}
                    </p>
                  )}
                </details>
              )}
              <div>
                <p className={`mb-1.5 text-xs font-medium ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  Eigener Behaviour Prompt{behaviorMode === 'blueprint' ? ' · gespeichert, derzeit nicht aktiv' : ' · aktiv'}
                </p>
              <textarea
                value={behaviorText}
                onChange={e => setBehaviorText(e.target.value)}
                disabled={adminDataLoading || !adminDataLoaded}
                rows={Math.max(10, Math.min(behaviorText.split('\n').length + 2, 24))}
                placeholder={`Zusätzliche Regeln nur für ${activeAgentLabel}, z. B. formeller antworten oder einen bestimmten Schwerpunkt setzen.`}
                className={`w-full px-3 py-2.5 text-sm rounded border outline-none resize-y font-mono transition-colors
                  disabled:cursor-wait disabled:opacity-70
                  ${d ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-blue-500'
                       : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'}`}
                style={{ height: behaviorText.trim().length > 600 ? '75vh' : undefined }}
              />
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                  {behaviorText.length} characters
                </span>
                <div className="flex items-center gap-3">
                  {behaviorSaved && <span className="text-xs text-green-400">Saved</span>}
                  <button
                    onClick={handleSaveBehavior}
                    disabled={loading || adminDataLoading || !adminDataLoaded}
                    className={`px-4 py-1.5 text-sm font-medium rounded transition-colors
                      ${loading || adminDataLoading || !adminDataLoaded ? 'opacity-50 cursor-wait' : ''}
                      ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                  >
                    {adminDataLoading ? 'Lädt...' : loading ? 'Speichert...' : 'Speichern & eigenen Prompt aktivieren'}
                  </button>
                </div>
              </div>

              <div className={`pt-4 border-t ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="mb-3">
                  <h3 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>Onboarding-Gedächtnis</h3>
                  <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    Angenommene Erkenntnisse aus dem Onboarding von {activeAgentLabel}, getrennt vom normalen Behavior Modifier
                  </p>
                </div>
                <textarea
                  value={onboardingMemoryText}
                  onChange={e => setOnboardingMemoryText(e.target.value)}
                  disabled={adminDataLoading || !adminDataLoaded}
                  rows={Math.max(8, Math.min(onboardingMemoryText.split('\n').length + 2, 20))}
                  placeholder={`Hier erscheinen angenommene Onboarding-Erinnerungen von ${activeAgentLabel} und können separat editiert werden.`}
                  className={`w-full px-3 py-2.5 text-sm rounded border outline-none resize-y font-mono transition-colors
                    disabled:cursor-wait disabled:opacity-70
                    ${d ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-blue-500'
                         : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'}`}
                />
                <div className="mt-3 flex items-center justify-between">
                  <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    {onboardingMemoryText.length} characters
                  </span>
                  <div className="flex items-center gap-3">
                    {onboardingMemorySaved && <span className="text-xs text-green-400">Saved</span>}
                    <button
                      onClick={handleSaveOnboardingMemory}
                      disabled={loading || adminDataLoading || !adminDataLoaded}
                      className={`px-4 py-1.5 text-sm font-medium rounded transition-colors
                        ${loading || adminDataLoading || !adminDataLoaded ? 'opacity-50 cursor-wait' : ''}
                        ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                    >
                      {adminDataLoading ? 'Loading...' : loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Vocabulary Tab ─── */}
        {!superadminOnly && activeTab === 'vocabulary' && (
          <div className="space-y-4">
            {/* Add/Edit form */}
            <div className={`rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <div className={`px-5 py-3 border-b ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                <h2 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                  {editingIndex !== null ? 'Edit Word' : 'Add Word'}
                </h2>
                <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                  Gilt gemeinsam für alle Avatare von {activeCustomerLabel}: Begriffe, STT-Aliasse und Aussprache-Regeln müssen nur einmal gepflegt werden.
                </p>
              </div>
              <div className="p-5">
                {vocabNotice && (
                  <div className={`mb-4 rounded border px-3 py-2 text-xs ${d ? 'border-amber-700/60 bg-amber-950/30 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                    {vocabNotice}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Word</label>
                    <input
                      type="text"
                      value={newWord}
                      onChange={e => setNewWord(e.target.value)}
                      placeholder="e.g. Synaworks"
                      className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                        ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Context</label>
                    <input
                      type="text"
                      value={newContext}
                      onChange={e => setNewContext(e.target.value)}
                      placeholder="e.g. SAP consulting company"
                      className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                        ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Output Pronunciation</label>
                    <input
                      type="text"
                      value={newPronunciation}
                      onChange={e => setNewPronunciation(e.target.value)}
                      placeholder="e.g. next-I-T"
                      className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                        ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Input Aliases</label>
                    <input
                      type="text"
                      value={newInputAliases}
                      onChange={e => setNewInputAliases(e.target.value)}
                      placeholder="e.g. Cineworks, Sina Works"
                      className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                        ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${d ? 'text-gray-400' : 'text-gray-600'}`}>Voice Replacement</label>
                    <input
                      type="text"
                      value={newVoiceReplacement}
                      onChange={e => setNewVoiceReplacement(e.target.value)}
                      placeholder="e.g. Cynerworks"
                      className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors
                        ${d ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                    />
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleAddWord}
                    disabled={!newWord.trim() || loading || adminDataLoading || !adminDataLoaded}
                    className={`px-4 py-1.5 text-sm font-medium rounded transition-colors
                      ${!newWord.trim() || loading || adminDataLoading || !adminDataLoaded ? 'opacity-40 cursor-not-allowed' : ''}
                      ${d ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                  >
                    {adminDataLoading ? 'Loading...' : loading ? 'Saving...' : editingIndex !== null ? 'Update' : 'Add'}
                  </button>
                  {editingIndex !== null && (
                    <button
                      onClick={() => { setEditingIndex(null); setNewWord(''); setNewContext(''); setNewPronunciation(''); setNewInputAliases(''); setNewVoiceReplacement(''); }}
                      className={`px-4 py-1.5 text-sm rounded transition-colors
                        ${d ? 'text-gray-400 hover:text-gray-200 border border-gray-700' : 'text-gray-500 hover:text-gray-700 border border-gray-300'}`}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Word list table */}
            <div className={`rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <div className={`px-5 py-3 border-b flex items-center justify-between ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                <span className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                  {vocabWords.length} word{vocabWords.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-3">
                  {vocabSaved && <span className="text-xs text-green-400">Saved</span>}
                </div>
              </div>
              {vocabWords.length === 0 ? (
                <div className={`p-8 text-center text-sm ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                  No vocabulary words yet. Add one above.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={d ? 'border-b border-gray-800' : 'border-b border-gray-200'}>
                        <th className={`px-5 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Word</th>
                        <th className={`px-5 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Context</th>
                        <th className={`px-5 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Input Aliases</th>
                        <th className={`px-5 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Output Pronunciation</th>
                        <th className={`px-5 py-2 text-left text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Voice Replacement</th>
                        <th className={`px-5 py-2 text-right text-xs font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vocabWords.map((w, i) => (
                        <tr
                          key={i}
                          className={`${d ? 'border-b border-gray-800/50 hover:bg-gray-800/40' : 'border-b border-gray-100 hover:bg-gray-50'} transition-colors`}
                        >
                          <td className={`px-5 py-2.5 font-medium ${d ? 'text-gray-200' : 'text-gray-800'}`}>{w.word}</td>
                          <td className={`px-5 py-2.5 ${d ? 'text-gray-400' : 'text-gray-600'}`}>{w.context}</td>
                          <td className={`px-5 py-2.5 font-mono text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{(w.input_aliases || []).join(', ') || '-'}</td>
                          <td className={`px-5 py-2.5 font-mono text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{w.output_pronunciation || w.pronunciation}</td>
                          <td className={`px-5 py-2.5 font-mono text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>{w.voice_replacement || '-'}</td>
                          <td className="px-5 py-2.5 text-right whitespace-nowrap">
                            <button
                              onClick={() => handleEditWord(i)}
                              disabled={loading}
                              className={`text-xs px-2 py-0.5 rounded mr-1 transition-colors
                                ${loading ? 'opacity-40 cursor-not-allowed' : ''}
                                ${d ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-500 hover:bg-blue-50'}`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleRemoveWord(i)}
                              disabled={loading}
                              className={`text-xs px-2 py-0.5 rounded transition-colors
                                ${loading ? 'opacity-40 cursor-not-allowed' : ''}
                                ${d ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Knowledge Base Tab ─── */}
        {!superadminOnly && activeTab === 'knowledgebase' && (
          <div className="flex flex-col gap-4">

            <div className={`order-1 rounded-lg border px-5 py-4 ${d ? 'border-blue-800/50 bg-blue-950/25' : 'border-blue-200 bg-blue-50'}`}>
              <h2 className={`text-sm font-semibold ${d ? 'text-blue-100' : 'text-blue-900'}`}>
                Knowledgebase von {activeAgentLabel}
              </h2>
              <p className={`mt-1 text-xs leading-5 ${d ? 'text-blue-200/70' : 'text-blue-800'}`}>
                Dokumente, Chunks und Zusammenfassung gelten ausschließlich für diesen Avatar. Andere Avatare von {activeCustomerLabel} verwenden ihre eigene Knowledgebase.
              </p>
            </div>

            {/* Versioned website import */}
            <div className={`order-5 rounded-lg border ${d ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <button
                type="button"
                onClick={() => setDeepScrapeExpanded(current => !current)}
                aria-expanded={deepScrapeExpanded}
                className={`flex w-full items-center justify-between gap-4 rounded-lg px-5 py-3 text-left transition-colors ${d ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'}`}
              >
                <span>
                  <span className={`block text-sm font-semibold ${d ? 'text-gray-100' : 'text-gray-900'}`}>Deepscrape Knowledgebase</span>
                  <span className={`mt-0.5 block text-xs ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    Website-Inhalte als neues, versioniertes Dokument hinzufügen
                  </span>
                </span>
                <span className={`shrink-0 text-xs font-medium ${d ? 'text-blue-400' : 'text-blue-600'}`}>
                  {deepScrapeExpanded ? 'Einklappen ▲' : 'Öffnen ▼'}
                </span>
              </button>

              {deepScrapeExpanded && (
                <>
              <form
                className={`space-y-4 border-t px-5 py-4 ${d ? 'border-gray-800' : 'border-gray-200'}`}
                onSubmit={(event) => { event.preventDefault(); handleStartDeepScrape(); }}
              >
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_120px_120px]">
                  <label className={`text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                    Start-URL
                    <input
                      type="url"
                      required
                      value={deepScrapeUrl}
                      onChange={event => setDeepScrapeUrl(event.target.value)}
                      placeholder="https://unternehmen.de"
                      className={`mt-1 w-full rounded border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 ${d ? 'border-gray-700 bg-gray-950 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </label>
                  <label className={`text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                    Max. Seiten
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={deepScrapeMaxPages}
                      onChange={event => setDeepScrapeMaxPages(Math.max(1, Math.min(500, Number(event.target.value) || 1)))}
                      className={`mt-1 w-full rounded border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 ${d ? 'border-gray-700 bg-gray-950 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </label>
                  <label className={`text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                    Linktiefe
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={deepScrapeMaxDepth}
                      onChange={event => setDeepScrapeMaxDepth(Math.max(1, Math.min(12, Number(event.target.value) || 1)))}
                      className={`mt-1 w-full rounded border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 ${d ? 'border-gray-700 bg-gray-950 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </label>
                </div>

                <details className={`rounded border px-3 py-2 ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                  <summary className={`cursor-pointer text-xs font-medium ${d ? 'text-gray-300' : 'text-gray-700'}`}>Erweiterte Regeln</summary>
                  <div className="mt-3 grid gap-3 lg:grid-cols-3">
                    <label className={`text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      Erlaubte Subdomains
                      <textarea
                        rows={2}
                        value={deepScrapeSubdomains}
                        onChange={event => setDeepScrapeSubdomains(event.target.value)}
                        placeholder="docs.unternehmen.de"
                        className={`mt-1 w-full rounded border px-3 py-2 outline-none ${d ? 'border-gray-700 bg-gray-950 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                      />
                    </label>
                    <label className={`text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      URL muss enthalten
                      <textarea
                        rows={2}
                        value={deepScrapeIncludePatterns}
                        onChange={event => setDeepScrapeIncludePatterns(event.target.value)}
                        placeholder="/leistungen/, /produkte/"
                        className={`mt-1 w-full rounded border px-3 py-2 outline-none ${d ? 'border-gray-700 bg-gray-950 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                      />
                    </label>
                    <label className={`text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      URL ausschließen
                      <textarea
                        rows={2}
                        value={deepScrapeExcludePatterns}
                        onChange={event => setDeepScrapeExcludePatterns(event.target.value)}
                        placeholder="/karriere/, /blog/"
                        className={`mt-1 w-full rounded border px-3 py-2 outline-none ${d ? 'border-gray-700 bg-gray-950 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                      />
                    </label>
                  </div>
                  <label className={`mt-3 flex items-start gap-2 text-xs ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                    <input
                      type="checkbox"
                      checked={deepScrapeIncludePdfs}
                      onChange={event => setDeepScrapeIncludePdfs(event.target.checked)}
                      className="mt-0.5"
                    />
                    Verlinkte PDFs bis 20 MB ebenfalls indexieren
                  </label>
                </details>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className={`text-xs ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    robots.txt, interne Netzwerksperren, Größenlimit und eine schonende Abrufrate werden automatisch erzwungen.
                  </p>
                  <button
                    type="submit"
                    disabled={deepScrapeLoading || deepScrapeRuns.some(run => ['queued', 'running'].includes(run.status) && run.normalized_root_url === deepScrapeUrl.trim())}
                    className={`rounded px-4 py-2 text-sm font-medium text-white transition-colors ${deepScrapeLoading ? 'cursor-wait bg-blue-400' : 'bg-blue-600 hover:bg-blue-500'}`}
                  >
                    {deepScrapeLoading ? 'Wird eingeplant…' : 'Deepscrape Knowledgebase'}
                  </button>
                </div>

                {deepScrapeMessage && (
                  <div className={`rounded border px-3 py-2 text-xs ${d ? 'border-blue-900/60 bg-blue-950/30 text-blue-200' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
                    {deepScrapeMessage}
                  </div>
                )}
              </form>

              {deepScrapeRuns.length > 0 && (
                <div className={`border-t px-5 py-4 ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className={`text-xs font-semibold uppercase tracking-wide ${d ? 'text-gray-400' : 'text-gray-600'}`}>Letzte Läufe</h4>
                    <button type="button" onClick={loadDeepScrapes} className={`text-xs ${d ? 'text-blue-400' : 'text-blue-600'}`}>Aktualisieren</button>
                  </div>
                  <div className="space-y-2">
                    {deepScrapeRuns.slice(0, 8).map(run => {
                      const isActiveVersion = run.active_run_id === run.id;
                      const inProgress = ['queued', 'running'].includes(run.status);
                      return (
                        <div key={run.id} className={`rounded border px-3 py-3 ${d ? 'border-gray-800 bg-gray-950/40' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${run.status === 'succeeded' ? 'bg-green-100 text-green-700' : run.status === 'partial' ? 'bg-amber-100 text-amber-800' : run.status === 'failed' ? 'bg-red-100 text-red-700' : d ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                                  {deepScrapeStatusLabel(run.status)}
                                </span>
                                {isActiveVersion && <span className="rounded bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">Aktive Version</span>}
                              </div>
                              <p className={`mt-1 max-w-3xl truncate text-xs ${d ? 'text-gray-300' : 'text-gray-700'}`}>{run.root_url || run.normalized_root_url}</p>
                              <p className={`mt-1 text-[11px] ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                                {run.pages_discovered || 0} gefunden · {run.pages_fetched || 0} gelesen · {run.pages_indexed || 0} indexiert · {run.chunks_created || 0} Chunks · {run.error_count || 0} Hinweise
                              </p>
                              {run.error_message && <p className="mt-1 text-[11px] text-red-500">{run.error_message}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              {inProgress && (
                                <button type="button" onClick={() => handleCancelDeepScrape(run.id)} className="rounded border border-red-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50">Abbrechen</button>
                              )}
                              {!inProgress && !isActiveVersion && ['succeeded', 'partial'].includes(run.status) && (
                                <button type="button" onClick={() => handleActivateDeepScrape(run.id)} className={`rounded border px-2 py-1 text-xs ${d ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Version aktivieren</button>
                              )}
                            </div>
                          </div>
                          {inProgress && (
                            <div className={`mt-3 h-1.5 overflow-hidden rounded-full ${d ? 'bg-gray-800' : 'bg-gray-200'}`}>
                              <div
                                className="h-full rounded-full bg-blue-500 transition-all"
                                style={{ width: `${Math.max(3, Math.min(100, ((run.pages_fetched || 0) / Math.max(1, run.config?.max_pages || 250)) * 100))}%` }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
                </>
              )}
            </div>

            {/* Upload zone */}
            <div
              className={`order-2 rounded-lg border-2 border-dashed transition-colors cursor-pointer
                ${uploadDragging
                  ? (d ? 'border-blue-500 bg-blue-900/20' : 'border-blue-400 bg-blue-50')
                  : (d ? 'border-gray-700 hover:border-gray-600' : 'border-gray-300 hover:border-gray-400')}
              `}
              onClick={() => { setUploadStatus({ type: 'idle', message: '' }); uploadInputRef.current?.click(); }}
              onDragOver={e => { e.preventDefault(); setUploadDragging(true); }}
              onDragLeave={() => setUploadDragging(false)}
              onDrop={e => {
                e.preventDefault();
                setUploadDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) handleUploadFile(file);
              }}
            >
              <input
                ref={uploadInputRef}
                type="file"
                className="hidden"
                accept=".txt,.md,.pdf,.docx,.odt,.mp3,.wav,.m4a,.mp4,.avi,.mov,.zip"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadFile(file);
                  e.target.value = '';
                }}
              />
              <div className="px-5 py-5 flex items-center gap-4">
                <div className={`text-2xl select-none ${uploadStatus.type === 'uploading' ? 'animate-spin' : ''}`}>
                  {uploadStatus.type === 'success' ? '✓' : uploadStatus.type === 'error' ? '✕' : '↑'}
                </div>
                <div>
                  <p className={`text-sm font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                    {uploadStatus.type === 'idle' ? 'Upload a document' : ''}
                    {uploadStatus.type === 'uploading' ? 'Processing…' : ''}
                    {uploadStatus.type === 'success' ? 'Done' : ''}
                    {uploadStatus.type === 'error' ? 'Upload failed' : ''}
                  </p>
                  <p className={`text-xs mt-0.5
                    ${uploadStatus.type === 'error' ? 'text-red-400' : ''}
                    ${uploadStatus.type === 'success' ? (d ? 'text-green-400' : 'text-green-600') : ''}
                    ${uploadStatus.type === 'idle' || uploadStatus.type === 'uploading' ? (d ? 'text-gray-500' : 'text-gray-400') : ''}
                  `}>
                    {uploadStatus.type === 'idle'
                      ? 'Drag & drop or click — PDF, DOCX, TXT, MP3, MP4, ZIP, …'
                      : uploadStatus.message}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary bar */}
            <div className={`order-3 rounded-lg border px-5 py-3 flex items-center justify-between ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <div>
                <span className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                  {documents.length} document{documents.length !== 1 ? 's' : ''}
                </span>
                <span className={`text-xs ml-3 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                  {totalChunks} total chunks
                </span>
              </div>
              <button
                onClick={loadDocuments}
                disabled={kbLoading}
                className={`text-xs px-3 py-1.5 rounded border transition-colors
                  ${kbLoading ? 'opacity-50' : ''}
                  ${d ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
              >
                {kbLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {/* Document list */}
            <div className={`order-4 rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              {kbLoading && documents.length === 0 ? (
                <div className="p-8 flex justify-center">
                  <svg className={`animate-spin h-5 w-5 ${d ? 'text-gray-500' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : documents.length === 0 ? (
                <div className={`p-8 text-center text-sm ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                  No documents found in knowledge base.
                </div>
              ) : (
                <div>
                  {documents.map((doc) => (
                    <div key={doc.document_name}>
                      {/* Document row */}
                      <div
                        className={`flex items-center justify-between px-5 py-3 cursor-pointer transition-colors border-b
                          ${selectedDoc === doc.document_name
                            ? (d ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-100')
                            : (d ? 'hover:bg-gray-800/40 border-gray-800/50' : 'hover:bg-gray-50 border-gray-100')
                          }`}
                        onClick={() => handleSelectDoc(doc.document_name)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`text-xs flex-shrink-0 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                            {selectedDoc === doc.document_name ? '\u25BC' : '\u25B6'}
                          </span>
                          <span className={`text-sm truncate ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                            {doc.document_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                          <span className={`text-xs tabular-nums ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                            {doc.chunk_count} chunk{doc.chunk_count !== 1 ? 's' : ''}
                          </span>
                          <span className={`text-xs tabular-nums ${d ? 'text-gray-600' : 'text-gray-300'}`}>
                            {(doc.total_chars / 1000).toFixed(0)}k chars
                          </span>
                          {confirmDelete === `doc:${doc.document_name}` ? (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <button
                                onClick={() => handleDeleteDocument(doc.document_name)}
                                disabled={loading}
                                className="text-xs px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-500"
                              >
                                {loading ? '...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className={`text-xs px-2 py-0.5 rounded ${d ? 'text-gray-400' : 'text-gray-500'}`}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete(`doc:${doc.document_name}`); }}
                              className={`text-xs px-2 py-0.5 rounded transition-colors
                                ${d ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                            >
                              Delete All
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded chunks for selected document */}
                      {selectedDoc === doc.document_name && docChunks.length > 0 && (
                        <div className={`${d ? 'bg-gray-950/50' : 'bg-gray-50/50'}`}>
                          {docChunks.map((chunk) => (
                            <div key={chunk.chunk_id} className={`border-b ${d ? 'border-gray-800/30' : 'border-gray-100'}`}>
                              {/* Chunk row */}
                              <div
                                className={`flex items-start gap-3 px-5 py-2.5 pl-12 cursor-pointer transition-colors
                                  ${expandedChunk?.chunk_id === chunk.chunk_id
                                    ? (d ? 'bg-gray-800/60' : 'bg-blue-50/50')
                                    : (d ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50')
                                  }`}
                                onClick={() => handleExpandChunk(chunk.chunk_id)}
                              >
                                <span className={`text-xs font-mono flex-shrink-0 w-8 text-right tabular-nums ${d ? 'text-gray-600' : 'text-gray-300'}`}>
                                  #{chunk.chunk_index}
                                </span>
                                <p className={`text-xs flex-1 line-clamp-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {chunk.text_preview}
                                </p>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`text-xs tabular-nums ${d ? 'text-gray-600' : 'text-gray-300'}`}>
                                    {chunk.chunk_tokens}tok
                                  </span>
                                  {confirmDelete === chunk.chunk_id ? (
                                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                      <button
                                        onClick={() => handleDeleteChunk(chunk.chunk_id)}
                                        disabled={loading}
                                        className="text-xs px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-500"
                                      >
                                        {loading ? '...' : 'Yes'}
                                      </button>
                                      <button
                                        onClick={() => setConfirmDelete(null)}
                                        className={`text-xs px-2 py-0.5 rounded ${d ? 'text-gray-400' : 'text-gray-500'}`}
                                      >
                                        No
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(chunk.chunk_id); }}
                                      className={`text-xs px-2 py-0.5 rounded transition-colors
                                        ${d ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Expanded chunk detail */}
                              {expandedChunk?.chunk_id === chunk.chunk_id && (
                                <div className={`px-5 py-4 pl-12 border-t ${d ? 'border-gray-800/30 bg-gray-900/50' : 'border-gray-100 bg-white'}`}>
                                  <div className="flex gap-4 mb-3 flex-wrap">
                                    <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                                      ID: <span className="font-mono">{expandedChunk.chunk_id.slice(0, 12)}...</span>
                                    </span>
                                    <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {expandedChunk.text.length.toLocaleString()} chars
                                    </span>
                                    {expandedChunk.metadata.chunk_tokens && (
                                      <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {expandedChunk.metadata.chunk_tokens} tokens
                                      </span>
                                    )}
                                  </div>
                                  <pre className={`text-xs leading-relaxed whitespace-pre-wrap overflow-auto max-h-80 font-mono p-3 rounded border
                                    ${d ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                                    {expandedChunk.text}
                                  </pre>
                                </div>
                              )}
                            </div>
                          ))}
                          {kbLoading && (
                            <div className={`px-5 py-4 pl-12 text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                              Loading chunks...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Knowledge Summary */}
            <div className={`order-6 rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <div className={`px-5 py-4 border-b ${d ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className={`text-sm font-semibold ${d ? 'text-gray-100' : 'text-gray-900'}`}>Knowledge Summary</h3>
                    <p className={`mt-1 text-xs max-w-3xl ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                      Optionaler Prompt-Block als mentales Modell der Knowledgebase. Leer lassen, wenn {activeAgentLabel} kein dauerhaftes Grundwissen daraus erhalten soll.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={loadKnowledgeSummary}
                      disabled={knowledgeSummaryGenerating || knowledgeSummarySaving}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50
                        ${d ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
                    >
                      Neu laden
                    </button>
                    <button
                      type="button"
                      onClick={handleGenerateKnowledgeSummary}
                      disabled={knowledgeSummaryGenerating || knowledgeSummarySaving || totalChunks === 0}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50
                        ${d ? 'border-blue-700 text-blue-300 hover:bg-blue-950/40' : 'border-blue-200 text-blue-700 hover:bg-blue-50'}`}
                    >
                      {knowledgeSummaryGenerating ? 'Generiert...' : 'Aus KB generieren'}
                    </button>
                    <button
                      type="button"
                      onClick={handleResetKnowledgeSummary}
                      disabled={knowledgeSummaryGenerating || knowledgeSummarySaving || knowledgeSummaryDraft === knowledgeSummarySavedText}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50
                        ${d ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
                    >
                      Zurücksetzen
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveKnowledgeSummary}
                      disabled={knowledgeSummaryGenerating || knowledgeSummarySaving || knowledgeSummaryDraft === knowledgeSummarySavedText}
                      className="text-xs px-3 py-1.5 rounded bg-green-600 text-white transition-colors hover:bg-green-500 disabled:opacity-50"
                    >
                      {knowledgeSummarySaving ? 'Speichert...' : 'Speichern'}
                    </button>
                  </div>
                </div>
                <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                  <span>{knowledgeSummaryDraft.trim() ? `${knowledgeSummaryDraft.length.toLocaleString()} Zeichen` : 'nicht aktiv'}</span>
                  <span>{countLines(knowledgeSummaryDraft)} Zeilen</span>
                  <span>Generiert: {formatDateTime(knowledgeSummaryMetadata?.generated_at || null)}</span>
                  <span>{knowledgeSummaryMetadata?.document_count || 0} Dokumente</span>
                  <span>{knowledgeSummaryMetadata?.chunk_count || 0} Chunks</span>
                </div>
              </div>

              <div className="p-5">
                {!knowledgeSummaryDraft && knowledgeSummaryBaseText && (
                  <div className={`mb-3 rounded border px-3 py-2 text-xs ${d ? 'border-gray-800 bg-gray-950 text-gray-500' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                    Basistext für neue Entwürfe:
                    <pre className="mt-2 whitespace-pre-wrap font-mono">{knowledgeSummaryBaseText}</pre>
                  </div>
                )}
                <textarea
                  value={knowledgeSummaryDraft}
                  onChange={e => setKnowledgeSummaryDraft(e.target.value)}
                  rows={14}
                  placeholder={knowledgeSummaryBaseText || '## Knowledge Summary'}
                  className={`w-full resize-y rounded border px-3 py-2 font-mono text-xs leading-relaxed outline-none transition-colors
                    ${d
                      ? 'bg-gray-950 border-gray-700 text-gray-200 placeholder-gray-700 focus:border-gray-500'
                      : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-500'}`}
                />
                <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 text-xs ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                  <span>
                    {knowledgeSummaryDraft.trim()
                      ? 'Wird nach dem Speichern in den System Prompt aufgenommen und im Prompt Preview angezeigt.'
                      : 'Leer gespeichert bedeutet: Der Block wird im finalen Prompt weggelassen.'}
                  </span>
                  {knowledgeSummaryMessage && (
                    <span className={knowledgeSummaryMessage.includes('konnte') ? 'text-red-500' : 'text-green-500'}>
                      {knowledgeSummaryMessage}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Conversations Tab ─── */}
        {!superadminOnly && activeTab === 'conversations' && (
          <div className="space-y-4">
            {/* Summary + search bar */}
            <div className={`rounded-lg border px-5 py-3 flex flex-wrap items-center gap-3 ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <span className={`text-sm font-semibold whitespace-nowrap ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                {convoSearchActive ? ' gefunden' : ''}
              </span>
              <div className="relative flex-1 min-w-[200px]">
                <input
                  type="text"
                  value={convoSearch}
                  onChange={(e) => setConvoSearch(e.target.value)}
                  placeholder="Konversationen durchsuchen…"
                  className={`w-full text-sm rounded border px-3 py-1.5 pr-8 outline-none transition-colors
                    ${d
                      ? 'bg-gray-950 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-gray-500'
                      : 'bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-400'}`}
                />
                {convoSearch && (
                  <button
                    type="button"
                    onClick={() => setConvoSearch('')}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 text-sm ${d ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                    aria-label="Suche löschen"
                  >
                    ✕
                  </button>
                )}
              </div>
              <button
                onClick={() => loadConversations(convoSearch)}
                disabled={convoLoading}
                className={`text-xs px-3 py-1.5 rounded border transition-colors whitespace-nowrap
                  ${convoLoading ? 'opacity-50' : ''}
                  ${d ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
              >
                {convoLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {/* Conversation list */}
            <div className={`rounded-lg border ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              {conversations.length === 0 ? (
                <div className={`p-8 text-center text-sm ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                  {convoLoading
                    ? 'Loading conversations...'
                    : convoSearchActive
                      ? `Keine Treffer für „${convoSearchActive}".`
                      : 'No conversations found.'}
                </div>
              ) : (
                <div>
                  {conversations.map((convo) => {
                    const modified = convo.last_modified ? new Date(convo.last_modified) : null;
                    const timeStr = modified
                      ? modified.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
                        + ' ' + modified.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                      : '';
                    const conversationParams = new URLSearchParams({ feedback: '1' });
                    if (convo.customer_slug) conversationParams.set('customer', convo.customer_slug);
                    if (convo.workspace_slug) conversationParams.set('workspace', convo.workspace_slug);
                    if (convo.agent_slug) conversationParams.set('agent', convo.agent_slug);
                    const conversationUrl = `/c/${convo.chat_id}?${conversationParams.toString()}`;
                    const agentLabel = convo.customer_slug
                      ? `${convo.customer_slug}/${convo.agent_slug ?? 'sally'}`
                      : convo.workspace_slug ?? 'Synaworks';
                    return (
                      <div
                        key={convo.chat_id}
                        className={`flex items-center justify-between px-5 py-3 cursor-pointer transition-colors border-b
                          ${d ? 'hover:bg-gray-800/40 border-gray-800/50' : 'hover:bg-gray-50 border-gray-100'}`}
                        onClick={() => window.open(conversationUrl, '_blank')}
                      >
                        <div className="min-w-0 flex-1 mr-4">
                          <p className={`text-sm truncate ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                            {convo.preview || '(kein Inhalt)'}
                          </p>
                          {convo.match_snippet && (
                            <p className={`text-xs mt-0.5 truncate italic ${d ? 'text-amber-400/80' : 'text-amber-700'}`}>
                              {convo.match_snippet}
                            </p>
                          )}
                          <div className="mt-0.5 flex flex-wrap items-center gap-2">
                            <p className={`text-xs font-mono ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                              {convo.chat_id.slice(0, 8)}...
                            </p>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                              convo.workspace_slug
                                ? d
                                  ? 'bg-emerald-900/30 text-emerald-300'
                                  : 'bg-emerald-50 text-emerald-700'
                                : d
                                  ? 'bg-gray-800 text-gray-400'
                                  : 'bg-gray-100 text-gray-500'
                            }`}>
                              {`Agentin: ${agentLabel}`}
                            </span>
                            {convo.username ? (
                              <span className={`text-[11px] px-2 py-0.5 rounded-full ${d ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-50 text-violet-700'}`}>
                                👤 {convo.username}
                              </span>
                            ) : (
                              <span className={`text-[11px] px-2 py-0.5 rounded-full ${d ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
                                anonym
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className={`text-xs tabular-nums ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                            {convo.message_count} msg
                          </span>
                          <span className={`text-xs tabular-nums ${d ? 'text-gray-600' : 'text-gray-300'}`}>
                            {timeStr}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            convo.has_feedback
                              ? d ? 'bg-blue-950/40 text-blue-700' : 'bg-blue-50 text-blue-300'
                              : d ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-600'
                          }`}>
                            Feedback
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversationFromList(convo.chat_id);
                            }}
                            className={`text-xs px-2 py-0.5 rounded transition-colors ${
                              d
                                ? 'bg-red-950/40 text-red-300 hover:bg-red-900/50'
                                : 'bg-red-50 text-red-600 hover:bg-red-100'
                            }`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Prompt Preview Tab ─── */}
        {!superadminOnly && activeTab === 'preview' && (
          <div className="space-y-3">
            {!preview ? (
              <div className={`rounded-lg border p-8 text-center ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                <p className={`text-sm ${d ? 'text-gray-500' : 'text-gray-400'}`}>Loading preview...</p>
              </div>
            ) : (
              <>
                {/* Header bar */}
                <div className={`rounded-lg border px-5 py-3 flex items-center justify-between ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                      Persona: <span className="font-mono">{preview.persona}</span>
                    </span>
                    <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                      {preview.total_lines?.toLocaleString() || countLines(preview.sections.map(section => section.content).join('\n')).toLocaleString()} lines · {preview.total_length.toLocaleString()} chars total · {preview.sections.length} sections
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleLoadPreview()}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors
                        ${d ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
                    >
                      Refresh
                    </button>
                    <button
                      onClick={handleTogglePromptPreviewExtended}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors
                        ${isPromptPreviewExtended
                          ? (d ? 'border-violet-700 text-violet-400 bg-violet-900/40' : 'border-violet-300 text-violet-600 bg-violet-100')
                          : (d ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400')
                        }`}
                    >
                      {isPromptPreviewExtended ? 'Advanced active' : 'Advanced'}
                    </button>
                  </div>
                </div>

                {/* Sections — each scrollable independently */}
                {preview.sections.map((section: PromptSection, idx: number) => {
                  const isEmpty = !section.content.trim();
                  const isCorePrompt = section.label === 'Core Prompt';
                  const isCallToActions = isCallToActionsSection(section);
                  const isCorePromptEditing = isCorePrompt && corePromptUnlocked;
                  const isCallToActionsEditing = isCallToActions && callToActionsUnlocked;
                  const lineCount = section.line_count ?? countLines(section.content);
                  const draftLineCount = countLines(corePromptDraft);
                  const callToActionsDraftLineCount = countLines(callToActionsDraft);
                  return (
                    <div key={idx} className={`rounded-lg border overflow-hidden ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                      {/* Section header */}
                      <div className={`px-5 py-2.5 border-b flex items-center justify-between ${d ? 'border-gray-800 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span className={`shrink-0 text-xs font-mono font-bold w-5 text-center rounded ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                            {idx + 1}
                          </span>
                          <div className="min-w-0">
                            <h3 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                              {section.label}
                            </h3>
                            {section.source && (
                              <p className={`mt-0.5 truncate text-[11px] font-mono ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                                {section.source}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {section.advanced && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${d ? 'bg-violet-900/40 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                              Advanced
                            </span>
                          )}
                          {!isEmpty && (
                            <span className={`text-xs tabular-nums ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                              {lineCount.toLocaleString()} lines · {section.content.length.toLocaleString()} chars
                            </span>
                          )}
                          {isCorePrompt && !corePromptUnlocked ? (
                            <button
                              type="button"
                              onClick={handleUnlockCorePrompt}
                              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                d
                                  ? 'border-gray-700 text-gray-400 hover:border-amber-700 hover:text-amber-300'
                                  : 'border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-700'
                              }`}
                              title="Core Prompt entsperren"
                            >
                              locked
                            </button>
                          ) : isCallToActions && !callToActionsUnlocked ? (
                            <button
                              type="button"
                              onClick={handleUnlockCallToActions}
                              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                d
                                  ? 'border-gray-700 text-gray-400 hover:border-amber-700 hover:text-amber-300'
                                  : 'border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-700'
                              }`}
                              title="Call to Actions entsperren"
                            >
                              locked
                            </button>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded-full border
                              ${isCorePromptEditing || isCallToActionsEditing || section.editable
                                ? (d ? 'border-green-800 text-green-400 bg-green-900/20' : 'border-green-300 text-green-700 bg-green-50')
                                : (d ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400')
                              }`}>
                              {isCorePromptEditing || isCallToActionsEditing ? 'unlocked' : section.editable ? 'editable' : 'locked'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Section content */}
                      {isCorePromptEditing ? (
                        <div className="px-5 py-4">
                          <div className={`mb-2 text-xs ${d ? 'text-amber-300' : 'text-amber-700'}`}>
                            Rohdatei wird editiert. Platzhalter wie <span className="font-mono">{'{cta_section}'}</span> müssen erhalten bleiben, wenn CTAs weiterhin automatisch eingefügt werden sollen.
                          </div>
                          <textarea
                            value={corePromptDraft}
                            onChange={(event) => setCorePromptDraft(event.target.value)}
                            rows={24}
                            className={`w-full resize-y rounded border px-3 py-3 font-mono text-xs leading-relaxed outline-none ${
                              d ? 'border-gray-700 bg-gray-950 text-gray-100 focus:border-amber-600' : 'border-gray-300 bg-white text-gray-900 focus:border-amber-400'
                            }`}
                          />
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <span className={`text-xs tabular-nums ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                              Draft: {draftLineCount.toLocaleString()} lines · {corePromptDraft.length.toLocaleString()} chars
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleCancelCorePromptEdit}
                                className={`rounded border px-3 py-1.5 text-xs font-medium transition-colors ${
                                  d ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                Abbrechen
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveCorePrompt}
                                disabled={corePromptSaving || !corePromptDraft.trim()}
                                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-500 disabled:cursor-wait disabled:bg-gray-600"
                              >
                                {corePromptSaving ? 'Speichert...' : 'Core Prompt speichern'}
                              </button>
                            </div>
                          </div>
                          {corePromptMessage && (
                            <div className={`mt-3 rounded px-3 py-2 text-xs ${d ? 'bg-gray-950 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                              {corePromptMessage}
                            </div>
                          )}
                        </div>
                      ) : isCallToActionsEditing ? (
                        <div className="px-5 py-4">
                          <div className={`mb-2 text-xs ${d ? 'text-amber-300' : 'text-amber-700'}`}>
                            Rohdatei wird editiert. Erwartet wird ein JSON-Array aus Objekten mit <span className="font-mono">key</span>, <span className="font-mono">name</span> und <span className="font-mono">url</span>.
                          </div>
                          <textarea
                            value={callToActionsDraft}
                            onChange={(event) => setCallToActionsDraft(event.target.value)}
                            rows={18}
                            className={`w-full resize-y rounded border px-3 py-3 font-mono text-xs leading-relaxed outline-none ${
                              d ? 'border-gray-700 bg-gray-950 text-gray-100 focus:border-amber-600' : 'border-gray-300 bg-white text-gray-900 focus:border-amber-400'
                            }`}
                          />
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <span className={`text-xs tabular-nums ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                              Draft: {callToActionsDraftLineCount.toLocaleString()} lines · {callToActionsDraft.length.toLocaleString()} chars
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleRefreshCallToActions}
                                className={`rounded border px-3 py-1.5 text-xs font-medium transition-colors ${
                                  d ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                Neu laden
                              </button>
                              <button
                                type="button"
                                onClick={handleFormatCallToActions}
                                className={`rounded border px-3 py-1.5 text-xs font-medium transition-colors ${
                                  d ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                JSON formatieren
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelCallToActionsEdit}
                                className={`rounded border px-3 py-1.5 text-xs font-medium transition-colors ${
                                  d ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                Abbrechen
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveCallToActions}
                                disabled={callToActionsSaving || !callToActionsDraft.trim()}
                                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-500 disabled:cursor-wait disabled:bg-gray-600"
                              >
                                {callToActionsSaving ? 'Speichert...' : 'Call to Actions speichern'}
                              </button>
                            </div>
                          </div>
                          {callToActionsMessage && (
                            <div className={`mt-3 rounded px-3 py-2 text-xs ${d ? 'bg-gray-950 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                              {callToActionsMessage}
                            </div>
                          )}
                        </div>
                      ) : isEmpty ? (
                        <p className={`px-5 py-4 text-xs italic ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                          (empty — not included in prompt)
                        </p>
                      ) : (
                        <>
                          {isCorePrompt && corePromptMessage && (
                            <div className={`mx-5 mt-4 rounded px-3 py-2 text-xs ${d ? 'bg-gray-950 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                              {corePromptMessage}
                            </div>
                          )}
                          {isCallToActions && callToActionsMessage && (
                            <div className={`mx-5 mt-4 rounded px-3 py-2 text-xs ${d ? 'bg-gray-950 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                              {callToActionsMessage}
                            </div>
                          )}
                          <PromptTextBlock content={section.content} isDarkMode={d} />
                        </>
                      )}
                    </div>
                  );
                })}

                {preview.diagnostics && (
                  <div className="space-y-3 pt-3">
                    <div className={`rounded-lg border px-5 py-4 ${d ? 'bg-amber-950/20 border-amber-900/60' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <h2 className={`text-sm font-semibold ${d ? 'text-amber-100' : 'text-amber-900'}`}>
                          Prompt Diagnostics
                        </h2>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${d ? 'border-amber-800 text-amber-300' : 'border-amber-300 text-amber-700'}`}>
                          locked
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {Object.entries(preview.diagnostics.realtime_config).map(([key, value]) => (
                          <div key={key} className={`rounded border px-3 py-2 ${d ? 'border-amber-900/60 bg-gray-950/30' : 'border-amber-200 bg-white/70'}`}>
                            <div className={`text-[10px] font-mono uppercase tracking-wide ${d ? 'text-amber-500' : 'text-amber-600'}`}>
                              {key}
                            </div>
                            <div className={`text-xs font-mono break-all ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                              {value || '(empty)'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {preview.diagnostics.sections.map((section: PromptSection, idx: number) => {
                      const isEmpty = !section.content.trim();
                      const lineCount = section.line_count ?? countLines(section.content);
                      return (
                        <div key={`diagnostic-${idx}`} className={`rounded-lg border overflow-hidden ${d ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                          <div className={`px-5 py-2.5 border-b flex items-center justify-between
                            ${d ? 'border-gray-800 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="min-w-0">
                              <h3 className={`text-sm font-semibold ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                                {section.label}
                              </h3>
                              {section.source && (
                                <p className={`mt-0.5 text-[11px] font-mono truncate ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {section.source}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${d ? 'bg-violet-900/40 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                                Advanced
                              </span>
                              {!isEmpty && (
                                <span className={`text-xs tabular-nums ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                                  {lineCount.toLocaleString()} lines · {section.content.length.toLocaleString()} chars
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${d ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                                locked
                              </span>
                            </div>
                          </div>

                          {isEmpty ? (
                            <p className={`px-5 py-4 text-xs italic ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                              (empty)
                            </p>
                          ) : (
                            <PromptTextBlock content={section.content} isDarkMode={d} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
